import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, CreditCard, Banknote, FileText, MapPin, Phone, Mail, Clock, MessageSquare, CheckCircle2, CalendarDays, Loader2, Info, ChevronLeft, ChevronRight, Lock, Check, Truck, ClipboardCheck, Send } from 'lucide-react';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';
import { sendFormEmail } from '../utils/sendFormEmail';
import { Skeleton } from './Skeleton';
import { getZoneForPosition } from '../data/deliveryZones';
import { useT } from '../utils/i18n';
import { saveOrderForTracking, buildShareLink, generateOrderNumber } from '../utils/orderTracking';
import { PostOrderDropSitePhoto } from './DropSitePhotoUpload';
import { AddressAutocomplete } from './AddressAutocomplete';
import { getRegionMeta } from '../utils/region';
import { pushRecentOrder } from '../utils/recentOrders';
import { trackGoogleAdsPurchase } from '../utils/googleAds';
// Format a 10/11-digit phone string into (XXX) XXX-XXXX
function formatPhone(raw: string): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark') || name.includes('play chips')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
// 50/50 Mix is classified HEAVY (12 yd full load) even though the name
// reads "mix" with topsoil — the blend (sand + topsoil) ships in the same
// 12-yd truckload as rock and aggregates.
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return (
    // Fill dirt removed — heavy base material, ships 12 yd/load.
    // 50/50 Mix removed — heavy blend (sand + topsoil) ships 12 yd/load.
    n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips')
  );
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee
  };
}
type DrawerView = 'cart' | 'checkout' | 'success';
export function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    loadSavedCart,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo,
    promo,
    applyPromoCode,
    clearPromoCode
  } = useCart();
  const [promoInput, setPromoInput] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  // Auto-prefill promo input if user copied FIRST10 from the PromoBar
  useEffect(() => {
    if (isCartOpen && !promo.code) {
      try {
        const hint = sessionStorage.getItem('promo_hint');
        if (hint) {
          setPromoInput(hint);
          setShowPromoInput(true);
        }
      } catch {}
    }
  }, [isCartOpen, promo.code]);
  const navigate = useNavigate();
  const [view, setView] = useState<DrawerView>('cart');
  // True after the user tries to submit the cart form with missing/invalid
  // required fields. Switches the guidance ring (green, "fill this next") to
  // an error ring (red, "this is missing") on every invalid field, and stays
  // on until the user advances successfully.
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  // Checkout form state
  const [addressInput, setAddressInput] = useState(deliveryAddress);
  const [addressError, setAddressError] = useState('');
  const wasCartOpenRef = useRef(false);
  // Cart delivery data is shared state, but this field is local so shoppers
  // can edit it without a re-render on every keystroke. Always refresh it at
  // the start of a new cart session: an Instant Estimator handoff supplies a
  // newly verified address, zone, and fee immediately before opening the cart.
  // Restricting this sync to the open transition preserves any active edit once
  // the shopper is already inside the drawer.
  useEffect(() => {
    const isOpening = isCartOpen && !wasCartOpenRef.current;
    if (isOpening) {
      setAddressInput(deliveryAddress);
      setAddressError('');
    }
    wasCartOpenRef.current = isCartOpen;
  }, [isCartOpen, deliveryAddress]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [instructions, setInstructions] = useState('');
  // dumpLocation dropdown was removed — the dump-location and special
  // instructions fields were merged into a single free-text
  // "Delivery Instructions" textarea (see the JSX below). All downstream
  // emails, SMS, and order tracking now use `instructions` as the single
  // source of truth for delivery directions.
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  // Tooltip explaining why same-day morning isn't guaranteed. Opens when
  // the customer clicks the "Not guaranteed" chip inside the time pill.
  // Kept separate from `showTimeDropdown` so the explanation can be read
  // without closing the time selector or vice-versa.
  const [showNotGuaranteedInfo, setShowNotGuaranteedInfo] = useState(false);
  // The dismissible "You don't need to be home" reassurance card was
  // replaced by an inline reassurance line above the Delivery Instructions
  // textarea, so no separate state or persistence is needed anymore.
  // Dump-location dropdown was removed in favor of a single free-text
  // "Delivery Instructions" textarea, so the outside-click handler that
  // previously closed the dropdown is no longer needed.
  // If a previously chosen time slot becomes invalid for the selected day
  // (e.g. user picks Afternoon, then switches to a Saturday — Sat ends 1 PM
  // so afternoon is closed), clear it so they re-select.
  useEffect(() => {
    if (!deliveryDate || !deliveryTime) return;
    const dow = new Date(deliveryDate + 'T12:00:00').getDay();
    if (dow === 0) {
      setDeliveryTime('');
    } else if (dow === 6 && deliveryTime === 'afternoon') {
      setDeliveryTime('');
    }
  }, [deliveryDate, deliveryTime]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'delivery'>('delivery');
  const [payOnDeliverySubMethod, setPayOnDeliverySubMethod] = useState<'cash' | 'card'>('cash');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [orderSummaryData, setOrderSummaryData] = useState<any>(null);
  // Surfaced when the order email/submission fails so the customer sees a
  // real error (and a way to retry / call) instead of a fake success screen.
  const [orderError, setOrderError] = useState<string>('');
  // New features state
  const [smsOptIn, setSmsOptIn] = useState(true);
  const {
    totalDeliveryFee
  } = useCart();
  // Material yards = what the customer actually needs. Billed yards
  // (totalYards from context) is what we charge for.
  const materialYards = items.reduce((sum, item) => sum + item.quantity, 0);
  const yardsBilledExtra = 0;
  // WA combined sales tax estimate for Pierce/King area (~10.1%) on materials only.
  // Delivery freight is generally non-taxable in WA when separately stated.
  const TAX_RATE = 0.101;
  // Saturday delivery surcharge — $40 per delivery when the order falls on a Saturday.
  // Multi-load orders pay the weekend surcharge on each load, matching how
  // the base delivery fee scales by deliveryCount.
  const SATURDAY_SURCHARGE_PER_LOAD = 40;
  const isSaturdayDelivery = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() === 6 : false;
  const saturdaySurcharge = isSaturdayDelivery ? SATURDAY_SURCHARGE_PER_LOAD * deliveryCount : 0;
  const discountedSubtotal = Math.max(0, totalPrice - promo.discount);
  // WA combined sales tax (~10.1%) on materials only. Delivery freight is
  // separately stated and remains non-taxable.
  const estimatedTax = Math.round(discountedSubtotal * TAX_RATE * 100) / 100;
  const finalTotal = discountedSubtotal + totalDeliveryFee + saturdaySurcharge + estimatedTax;
  // Same-day cutoff: 12 PM (noon) Mon–Sat. Before noon on a business day,
  // today is still selectable for afternoon / flexible delivery.
  // Otherwise the next business day (skipping Sunday).
  const minDate = (() => {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun, 6=Sat
    const hour = now.getHours();
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const target = new Date(now);
    target.setHours(0, 0, 0, 0);
    // Sunday: dispatch closed, earliest delivery is Monday
    if (dow === 0) {
      target.setDate(target.getDate() + 1);
    } else if (hour >= 12) {
      // After noon cutoff on a business day → next day
      target.setDate(target.getDate() + 1);
      // If next day is Sunday, push to Monday
      if (target.getDay() === 0) target.setDate(target.getDate() + 1);
    }
    return fmt(target);
  })();
  // Refs for auto-advance
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  const instructionsRef = useRef<HTMLTextAreaElement>(null);
  // Wraps the whole Delivery Instructions section (label + textarea + inline
  // "Done — Continue" button). After date/time are picked we scroll THIS to the
  // top of the form so the label, field, and the Done button are all visible
  // above the mobile keyboard — not just the centered textarea.
  const instructionsSectionRef = useRef<HTMLDivElement>(null);
  // The primary "Proceed to Checkout" CTA — used to scroll the user past the
  // mobile keyboard when they finish typing delivery instructions.
  const checkoutCtaRef = useRef<HTMLButtonElement>(null);
  // True while the delivery-instructions textarea holds focus. Drives the
  // inline "Done" button that lets mobile users dismiss the keyboard and jump
  // straight to the checkout CTA instead of manually closing the keyboard and
  // scrolling the half-covered screen.
  const [instructionsFocused, setInstructionsFocused] = useState(false);
  const t = useT();
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim() || view !== 'cart') {
      if (!addressInput.trim()) {
        clearDeliveryInfo();
        setAddressError('');
      }
      return;
    }
    // If the address text matches the already-validated address (from the
    // Hero estimator or the Delivery Zones map page), trust the upstream
    // zone — don't re-geocode. Re-geocoding ambiguous addresses (e.g. a
    // street name without a city) can resolve to a different point and
    // produce a different zone than what the customer was originally
    // shown, which would silently change their delivery fee.
    if (addressInput.trim() === deliveryAddress.trim() && deliveryZone !== null) {
      setAddressError('');
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        // viewbox keeps ambiguous addresses anchored to our service area
        // (roughly South King + Pierce County). bounded=1 forces results
        // to fall inside the box, preventing wrong-city resolutions.
        // Box: left,top,right,bottom = lon_min,lat_max,lon_max,lat_min
        const viewbox = '-122.75,47.75,-121.65,47.00';
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us&viewbox=${viewbox}&bounded=1`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address (include the city).');
          clearDeliveryInfo();
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const zoneInfo = getZoneInfo(lat, lon);
          setDeliveryInfo(addressInput, zoneInfo.fee, zoneInfo.zone);
          // Auto-advance to name if address is valid
          if (!name) {
            setTimeout(() => nameRef.current?.focus(), 100);
          }
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        clearDeliveryInfo();
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput, view, deliveryAddress, deliveryZone, setDeliveryInfo, clearDeliveryInfo, setIsCalculatingDelivery]);
  const formatPhone = (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return val;
    if (!match[2]) return match[1];
    if (!match[3]) return `(${match[1]}) ${match[2]}`;
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  };
  const isAddressValid = addressInput && !addressError && !isCalculatingDelivery;
  const isNameValid = name.trim().length > 1;
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isDateValid = !!deliveryDate;
  const handleContinueShopping = () => {
    closeCart();
    setView('cart');
    const el = document.getElementById('materials');
    if (el) {
      setTimeout(() => el.scrollIntoView({
        behavior: 'smooth'
      }), 300);
    }
  };
  const handleCheckout = () => {
    // Validate required fields before proceeding
    if (!addressInput.trim() || !name.trim() || !phone.trim() || !email.trim() || !deliveryDate || !deliveryTime) {
      // We could set an error state here, but for now we'll just rely on HTML5 form validation
      // by triggering a form submit on a hidden button, or just alert.
      // Since we are moving this to a form, we should let the form handle the submit.
      return;
    }
    setView('checkout');
  };
  const handleCartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Block advancing to checkout if ANY required cart-step field is
    // missing. Delivery instructions in particular need to be enforced
    // here — if they're skipped, the customer reaches the checkout view
    // with a silently-disabled Place Order button and no obvious way
    // back to fix the missing field. Catching it here puts focus
    // directly on the unfilled input instead.
    if (!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !deliveryTime || !instructions.trim()) {
      setHasAttemptedSubmit(true);
      // Focus and scroll to the first missing field so the user is taken
      // directly to what they need to fix.
      const firstInvalid: HTMLElement | null = !isAddressValid ? document.querySelector<HTMLInputElement>('input[autocomplete="street-address"]') : !isNameValid ? nameRef.current : !isPhoneValid ? phoneRef.current : !isEmailValid ? emailRef.current : !isDateValid ? dateBtnRef.current : !deliveryTime ? timeBtnRef.current : !instructions.trim() ? instructionsRef.current : null;
      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setTimeout(() => firstInvalid.focus({
          preventScroll: true
        }), 350);
      }
      return;
    }
    setHasAttemptedSubmit(false);
    setView('checkout');
  };
  const handleBackToCart = () => {
    setView('cart');
  };
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validation guard — the Place Order button's `disabled` attribute
    // only blocks mouse clicks. A keyboard Enter press inside any of
    // the text inputs can still trigger this form's onSubmit, which
    // previously let an order through with missing fields (most
    // commonly: empty delivery instructions). Re-check the same rules
    // the button uses, scroll the first missing field into view, and
    // bail out before any payment / order side-effects fire.
    if (!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !instructions.trim()) {
      setHasAttemptedSubmit(true);
      const firstInvalid: HTMLElement | null = !isAddressValid ? document.querySelector<HTMLInputElement>('input[autocomplete="street-address"]') : !isNameValid ? nameRef.current : !isPhoneValid ? phoneRef.current : !isEmailValid ? emailRef.current : !isDateValid ? dateBtnRef.current : !instructions.trim() ? instructionsRef.current : null;
      if (firstInvalid) {
        firstInvalid.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
        setTimeout(() => firstInvalid.focus({
          preventScroll: true
        }), 350);
      }
      return;
    }
    setIsProcessingPayment(true);
    try {
      const orderItems = items.map(item => {
        const isBark = isLightMaterial(item.product);
        const smallLoadThreshold = 10;
        const billableYards = item.quantity;
        const surcharge = (() => {
          if (billableYards < 3) return 1; // Flat $250 delivery fee covers economics
          if (billableYards >= smallLoadThreshold) return 1;
          return 1 + (smallLoadThreshold - billableYards) * 0.1;
        })();
        const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
        let total = billableYards * effectivePrice;
        const fullLoad = isBark ? 15 : 12;
        const bulkPct = item.quantity >= fullLoad * 4 ? 0.1 : item.quantity >= (isBark ? fullLoad : fullLoad * 2) ? 0.05 : 0;
        const hasBulk = bulkPct > 0;
        if (hasBulk) {
          total = Math.round(total * (1 - bulkPct));
        }
        const bulkNote = hasBulk ? ` (${Math.round(bulkPct * 100)}% bulk discount)` : '';
        const yardsLabel = `${item.quantity} yds`;
        return `${item.product} (${item.category}) — ${yardsLabel}${bulkNote} = ${total}`;
      }).join('\n');
      const deliveryDow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
      const isSatDelivery = deliveryDow === 6;
      const timeLabel = deliveryTime === 'morning' ? 'Morning (7-11 AM)' : deliveryTime === 'afternoon' ? isSatDelivery ? 'Afternoon (12-1 PM)' : 'Afternoon (12-4 PM)' : isSatDelivery ? 'Flexible (before 1 PM)' : 'Flexible';
      const deliveryFeeLabel = totalDeliveryFee > 0 ? `${totalDeliveryFee.toFixed(2)}` : deliveryZone === null ? 'Custom Quote' : 'FREE';
      const promoLine = promo.code && promo.discount > 0 ? `\nPromo Code: ${promo.code} (${promo.code === 'FAM20' ? '20' : '10'}% off materials, −${promo.discount.toFixed(2)})` : '';
      const saturdayLine = saturdaySurcharge > 0 ? `\nSaturday Delivery Surcharge: ${saturdaySurcharge.toFixed(2)}${deliveryCount > 1 ? ` (${deliveryCount} × ${SATURDAY_SURCHARGE_PER_LOAD.toFixed(2)})` : ''}` : '';
      const subMethodLabel = payOnDeliverySubMethod.charAt(0).toUpperCase() + payOnDeliverySubMethod.slice(1);
      const paymentLabel = paymentMethod === 'card' ? 'Pay Online' : `Pay on Delivery (${subMethodLabel})`;
      // Order-source analytics — tag the email so you can see whether the
      // customer built this order in the Instant Estimator, the Shopping Cart
      // (Product page / Materials hub), or a mix of both.
      const sourceTags = new Set(items.map(it => (it as any).source || 'cart'));
      // If every item came from the Hero estimator, this is a pure
      // estimator handoff. If sources are mixed, fall through to "Mixed"
      // so dispatch can see the order was a combo flow.
      const isEstimatorHandoff = sourceTags.size === 1 && sourceTags.has('estimator');
      const isMixedSource = sourceTags.size > 1;
      const orderSourceLabel = isEstimatorHandoff ? 'Instant Estimator' : isMixedSource ? 'Mixed (Estimator + Cart)' : 'Direct Cart';
      // Yards breakdown for the email body
      const yardsLine = `Total Yards: ${totalYards}`;
      const orderSummary = `Order Summary:\n${orderItems}\n\n${yardsLine}\nMaterials Total: ${totalPrice.toFixed(2)}${promoLine}\nDelivery Fee: ${deliveryFeeLabel}${saturdayLine}\nSales Tax (est. ${(TAX_RATE * 100).toFixed(1)}%): ${estimatedTax.toFixed(2)}\nOrder Total: ${finalTotal.toFixed(2)}\n\nOrder Source: ${orderSourceLabel}\n\nDelivery Address: ${addressInput}\nRequested Date: ${deliveryDate}\nTime Window: ${timeLabel}\nDelivery Instructions: ${instructions || 'Not specified'}\nPayment: ${paymentLabel}`;
      const phoneFormatted = (() => {
        const d = (phone || '').replace(/\D/g, '');
        const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
        return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : phone;
      })();
      // Build the order record up-front so we can include a live tracking
      // link inside the confirmation email body itself.
      // Order number format: 206-<CATEGORY>-<SEQ> (e.g. 206-BC-4821 for a
      // Base & Compaction order, 206-MX-4821 for a mixed-category cart).
      // The last four digits are randomized per order.
      const orderNumber = generateOrderNumber(items);
      // Unified brand: every order is 206Gravel regardless of which domain
      // the customer arrived on. getRegionMeta() always resolves to 206.
      const regionCode = getRegionMeta().code;
      const brandName = `${regionCode} Gravel`;
      const brandCompact = `${regionCode}Gravel`;
      const brandDomain = `${regionCode}gravel.com`;
      const trackedOrderPayload = {
        orderId: orderNumber,
        createdAt: Date.now(),
        address: addressInput,
        name,
        phone,
        email,
        date: deliveryDate,
        time: timeLabel,
        instructions,
        items: items.map(it => ({
          product: it.product,
          quantity: it.quantity,
          pricePerYard: it.pricePerYard
        })),
        totalDeliveryFee: deliveryFee,
        saturdaySurcharge: saturdaySurcharge || 0,
        estimatedTax,
        finalTotal
      };
      const trackLink = buildShareLink(trackedOrderPayload);
      const trackOrderSection = `TRACK YOUR ORDER\n\nOrder #: ${orderNumber}\n\nFollow your delivery in real time — confirmed, scheduled, dispatched, en route, delivered:\n${trackLink}\n\nBookmark this link or save this email. It works on any device, no login required.`;
      const whatsNext = `WHAT HAPPENS NEXT\n\n1. SMS Confirmation\n   A text confirmation will arrive at ${phoneFormatted} within a few minutes.\n\n2. Dispatch Review\n   Our team verifies your address, materials, and delivery window — usually within 1 business hour.\n\n3. Driver Dispatched\n   On delivery day, we'll text you a live tracking link the moment your driver is en route.\n\n4. Track Anytime\n   Check the status of your order at any time using your tracking link below.`;
      const howToPrepare = `HOW TO PREPARE FOR DELIVERY\n\n• Clear the dump zone — Mark a flat, accessible spot at least 10' wide x 20' long.\n• Driveway clearance — We need 12' of overhead clearance (watch for branches and wires).\n• Move vehicles & obstacles — Cars, trash bins, hoses, and toys should be clear of the path.\n• Surface protection — If dumping on a finished driveway, lay down plywood or a tarp to protect the surface.\n• Be reachable — Keep your phone nearby on delivery day in case the driver needs directions.\n• Have payment ready — If paying on delivery, please have cash, card, or check available.\n\nFull guide: https://${brandDomain}/delivery-prep`;
      // Property & vehicle safety — the same constraint shown on the order
      // confirmation screen next to the drop-site photo. Repeated here so the
      // customer has it in writing before delivery day.
      const propertySafety = `PROPERTY & VEHICLE SAFETY\n\nWe cannot drive across irrigated, wet, frozen, or landscaped areas to reach the dump spot. Please make sure your delivery instructions keep our truck on gravel or paved surfaces.`;
      // Referral pitch — moved here from the site-wide announcement bar so
      // it reaches paying customers (where it actually has ROI) instead of
      // showing a discount to every visitor. The friend isn't promised a
      // dedicated referral discount (the public FIRST10 already gives
      // first-timers 10% off, so a "FRIEND10" code would be a meaningless
      // duplicate). The real referral incentive is the $25 credit to the
      // original customer — that's clear money in their pocket per
      // referred order, no double-promo confusion.
      const referralSection = `SHARE ${brandCompact.toUpperCase()} — EARN $50 PER REFERRAL\n\nKnow someone else who needs gravel, topsoil, or bark?\n\n  Have them mention your name when they book.\n  Once their order is delivered, we'll credit $50 to your next delivery — automatically.\n\nNo expiration. No minimum. No limit on referrals.`;
      // De-duplicated the "call us" line — the trailing footer already
      // tells the customer to call (206) 619-3742 with questions, so the
      // referral section doesn't need to repeat it.
      const fullEmailBody = `Hi ${name}! Thank you for your order request with ${brandName}. We've received your details and will contact you shortly to verify your order and confirm delivery.\n\n${orderSummary}\n\n----------\n\n${trackOrderSection}\n\n----------\n\n${whatsNext}\n\n----------\n\n${howToPrepare}\n\n----------\n\n${propertySafety}\n\n----------\n\n${referralSection}\n\nIf you have any questions, call us at (206) 619-3742.\n\nWe look forward to serving you!\n- The ${brandName} Team`;
      // Clear any prior submission error before re-trying.
      setOrderError('');
      const emailOk = await sendFormEmail({
        subject: `${brandCompact} — New Order Submission`,
        to_name: name,
        from_name: brandName,
        'Order Items': orderItems,
        'Total Yards': String(totalYards),
        'Materials Total': `${totalPrice.toFixed(2)}`,
        'Promo Code': promo.code && promo.discount > 0 ? `${promo.code} (−${promo.discount.toFixed(2)})` : 'None',
        'Delivery Fee': deliveryFeeLabel,
        'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone}` : 'Outside standard zones',
        'Order Total': `${finalTotal.toFixed(2)}`,
        'Order Source': orderSourceLabel,
        Name: name,
        'Delivery Address': addressInput,
        Phone: phone,
        email: email,
        _replyto: email,
        _autoresponse: fullEmailBody,
        message: fullEmailBody,
        'Order Number': orderNumber,
        'Track Order Link': trackLink,
        'Track Order Section': trackOrderSection,
        'What Happens Next': whatsNext,
        'How To Prepare': howToPrepare,
        'Property & Vehicle Safety': propertySafety,
        'Delivery Date': deliveryDate,
        'Delivery Time': timeLabel,
        'Delivery Instructions': instructions || 'Not specified',
        'Drop-Site Photo': 'Pending — customer can upload from order success screen',
        'Payment Method': paymentLabel
      });
      // CRITICAL: only transition to the success view if EmailJS actually
      // accepted the order. Previously the code optimistically flipped
      // view='success' regardless of email outcome, which made it look
      // like the order went through even when dispatch never received it.
      if (!emailOk) {
        setOrderError("We couldn't send your order to dispatch just now. Please try again, or call us at (206) 619-3742 and we'll take it over the phone.");
        setIsProcessingPayment(false);
        return;
      }
      // Dispatch has accepted the order, so this is the authoritative purchase
      // confirmation point for Google Ads. The generated order number acts as
      // the transaction ID, which lets Google deduplicate any repeated event.
      let isNewCustomer = false;
      try {
        isNewCustomer = !localStorage.getItem('253gravel_last_order');
      } catch {
        // Tracking may still proceed if browser storage is unavailable; we
        // simply omit the first-order classification in that edge case.
      }
      trackGoogleAdsPurchase({
        transactionId: orderNumber,
        value: finalTotal,
        isNewCustomer
      });
      setOrderSummaryData({
        items: [...items],
        totalYards,
        materialYards,
        yardsBilledExtra,
        totalPrice,
        deliveryFeeLabel,
        saturdaySurcharge,
        estimatedTax,
        finalTotal,
        addressInput,
        deliveryDate,
        timeLabel,
        name,
        phone,
        email,
        orderNumber
      });
      // Append to the recent-orders list FIRST (up to 3, de-duped) so the
      // hero "Reorder last delivery" drawer can offer a choice between the
      // customer's standard loads. This must run BEFORE we overwrite the
      // single-order key below — pushRecentOrder seeds from that key, so
      // overwriting it first would clobber the previous order and we'd only
      // ever keep the latest one.
      pushRecentOrder({
        items: [...items],
        addressInput,
        name,
        phone,
        email
      });
      // Save the single most-recent order for backward-compat (other
      // surfaces and older devices still read this key).
      localStorage.setItem('253gravel_last_order', JSON.stringify({
        items: [...items],
        addressInput,
        name,
        phone,
        email
      }));
      // Burn the private FAM20 family code on this device once it's been
      // redeemed on a placed order — it can only be used a single time.
      if (promo.code === 'FAM20') {
        try {
          localStorage.setItem('253gravel_fam20_used', '1');
        } catch {}
      }
      // Save a complete tracked-order record so the customer can track it.
      try {
        saveOrderForTracking(trackedOrderPayload);
      } catch (e) {
        console.error('Could not save tracked order', e);
      }
      // Email accepted AND the success snapshot is staged — now it's safe
      // to clear the live cart and show the confirmation screen. Doing
      // both here (instead of waiting for handleCloseSuccess) means the
      // cart stays empty no matter HOW the customer dismisses the
      // success view (Done, X, backdrop tap, navigating away).
      setView('success');
      clearCart();
      // Tell the Hero instant estimator to return to its default state now
      // that the order is placed (it stays in sync across components via a
      // lightweight window event rather than shared state).
      window.dispatchEvent(new Event('estimatorReset'));
    } catch (err) {
      console.error('Order submission error:', err);
      setOrderError('Something went wrong placing your order. Please try again, or call us at (206) 619-3742.');
    } finally {
      setIsProcessingPayment(false);
    }
  };
  const handleCloseSuccess = () => {
    setView('cart');
    setAddressInput('');
    setName('');
    setPhone('');
    setEmail('');
    setDeliveryDate('');
    setDeliveryTime('');
    setInstructions('');
    // Note: the old setDumpLocation('') reset call lived here. The
    // dumpLocation state was removed when the dump-location dropdown
    // got merged into the free-text Delivery Instructions textarea
    // (see the comment near the top of this file). Leaving the setter
    // call behind threw "setDumpLocation is not defined" the moment
    // the customer closed the success screen.
    clearCart();
    closeCart();
    // Return the customer to the home page so they land on the Hero's
    // in-flight greeting — a blinking "order in progress" header with a
    // one-tap link to the live tracking page. usePersonalGreeting reads
    // the just-saved tracked order on mount and renders that surface.
    navigate('/');
    if (typeof window !== 'undefined') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const inputClass = 'cart-input w-full px-4 py-3.5 bg-white dark:bg-[#2e2e2e] backdrop-blur-sm border border-[#e5e5e5] dark:border-white/5 rounded-xl text-stone-900 dark:text-white placeholder-stone-500 dark:placeholder-stone-400 focus:ring-2 focus:ring-[#059F40]/50 focus:border-[#059F40] focus:shadow-[0_0_15px_rgba(5,159,64,0.2)] transition-all text-base font-medium dark:shadow-[inset_0_1px_2px_rgba(0,0,0,0.3)]';
  // Helper to build delivery zones URL with address
  const deliveryZonesUrl = (addr?: string) => {
    const params = addr?.trim() ? `?address=${encodeURIComponent(addr)}` : '';
    return `/delivery-zones${params}`;
  };
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };
  const handleReorder = () => {
    const lastOrderStr = localStorage.getItem('253gravel_last_order');
    if (lastOrderStr) {
      const lastOrder = JSON.parse(lastOrderStr);
      if (lastOrder.items && lastOrder.items.length > 0) {
        loadSavedCart(lastOrder.items);
        setAddressInput(lastOrder.addressInput || '');
        setName(lastOrder.name || '');
        setPhone(lastOrder.phone || '');
        setEmail(lastOrder.email || '');
      }
    }
  };
  const getDeliveryDayText = () => {
    if (!deliveryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const delDate = new Date(deliveryDate + 'T00:00:00');
    const diffTime = delDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Same-day delivery!';
    if (diffDays === 1) return 'Delivering tomorrow!';
    return `Delivering in ${diffDays} days`;
  };
  return <AnimatePresence data-id="element-229" data-mp="20q3sl34b10g4">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => {
        closeCart();
        setView('cart');
      }} className="fixed inset-0 bg-stone-900/40 dark:bg-black/70 backdrop-blur-md z-[60]" data-id="element-230" data-mp="20o0s1p5izox5" />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[70] pointer-events-none" role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-231" data-mp="2keuo0tqxgkx1">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className={
        // Glossy dark treatment: deep green-black panel with iridescent sheen,
        // inset highlights, and soft layered shadows.
        'w-full max-w-[900px] backdrop-blur-xl flex flex-col rounded-2xl max-h-[90dvh] overflow-hidden pointer-events-auto transition-colors duration-300 border-2 bg-[#f6f8f9] dark:bg-[#2e2e2e] border-[#d4ddcf] dark:border-[#111111] shadow-[0_2px_8px_rgba(110,140,106,0.12),0_24px_70px_-12px_rgba(110,140,106,0.28)] dark:shadow-[0_24px_70px_-12px_rgba(0,0,0,0.6)] relative'} role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-232" data-mp="25nlzknyrh5lh">
              {/* Header */}
              <div className={'flex items-center justify-between px-8 py-6 border-b shrink-0 transition-colors duration-300 relative z-10 ' + (view === 'checkout' ? 'border-[#6e8c6a]/30 dark:border-white/5 bg-gradient-to-b from-[#6e8c6a]/[0.10] to-transparent dark:from-white/[0.04] dark:to-transparent' : 'border-[#e5e5e5] dark:border-white/5 bg-gradient-to-b from-[#f5f5f5] to-transparent dark:from-white/[0.04] dark:to-transparent')} data-id="element-233" data-mp="2ka2nr2cjglis">
                <div className="flex items-center space-x-4" data-id="element-234" data-mp="qsww1r0j19am">
                  {view === 'checkout' && <button onClick={handleBackToCart} className="p-2 text-[#059F40] hover:text-[#0C2809] dark:text-[#22c95c] dark:hover:text-white transition-colors rounded-lg hover:bg-[#059F40]/15 mr-1" aria-label="Back to cart" data-id="element-235" data-mp="uv9qvkdqjmsm">
                      <ArrowLeft className="w-6 h-6" data-id="element-236" data-mp="34vi22kkx0nt" />
                    </button>}
                  <Truck className={'w-7 h-7 transition-colors ' + (view === 'checkout' ? 'text-[#059F40]' : 'text-brand-charcoal dark:text-white')} data-id="element-237" data-mp="74nuiqd3fqyg" />
                  <h2 id="cart-drawer-title" className={'font-heading text-xl font-bold transition-colors ' + (view === 'checkout' ? 'text-[#0C2809] dark:text-white' : 'text-brand-charcoal dark:text-white')} data-id="element-238" data-mp="c2fkheu6h300">
                    {view === 'cart' ? t('cart.title') : view === 'checkout' ? t('cart.checkout') : t('cart.orderConfirmed')}
                  </h2>
                  {view === 'checkout' && <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#059F40]/15 border border-[#059F40]/35 text-[10px] font-bold uppercase tracking-wider text-[#059F40] dark:text-[#22c95c]" data-id="element-239" data-mp="2dxjeahsw75lq">
                      <Lock className="w-3 h-3" data-id="element-240" data-mp="2mvd1n38oiowr" />
                      Secure Checkout
                    </span>}
                </div>
                <button onClick={() => {
              closeCart();
              setView('cart');
            }} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/10" aria-label="Close cart" data-id="element-241" data-mp="2wv3s8tu4cjrk">
                  <X className="w-6 h-6" data-id="element-242" data-mp="31m2fkddiyi4b" />
                </button>
              </div>
              {/* ===== CART VIEW ===== */}
              {view === 'cart' && <form id="cart-form" onSubmit={handleCartSubmit} className="flex-1 overflow-y-auto" data-id="element-243" data-mp="3fkagewdom3kr">
                  {/* Sticky Progress Stepper — only when there are items */}
                  {items.length > 0 && <div className="sticky top-0 z-[55] bg-[#fcfcfc]/85 dark:bg-[#2e2e2e] backdrop-blur-md border-b border-[#e5e5e5] dark:border-white/5 px-6 py-2.5" data-id="element-244" data-mp="3u6zd6qgju2ng">
                      <div className="flex items-center justify-between max-w-full" data-id="element-245" data-mp="3l7m25tcs160m">
                        {[{
                  id: 'address',
                  label: 'Address',
                  isComplete: isAddressValid,
                  isActive: !isAddressValid
                }, {
                  id: 'name',
                  label: 'Name',
                  isComplete: isNameValid,
                  isActive: isAddressValid && !isNameValid
                }, {
                  id: 'phone',
                  label: 'Phone',
                  isComplete: isPhoneValid,
                  isActive: isAddressValid && isNameValid && !isPhoneValid
                }, {
                  id: 'email',
                  label: 'Email',
                  isComplete: isEmailValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && !isEmailValid
                }, {
                  id: 'date',
                  label: 'Date',
                  isComplete: isDateValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid
                }, {
                  id: 'time',
                  label: 'Time',
                  isComplete: !!deliveryTime,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime
                }].map((step, idx, arr) => <Fragment key={step.id} data-id="element-246" data-mp="2vwecup0kaux3" data-mpi={idx}>
                            <div className="flex items-center gap-1.5 relative z-10 shrink-0" data-id="element-247" data-mp="2vxpher1u06p9">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-300 ${step.isComplete ? 'bg-[#059F40] text-white shadow-[0_0_8px_rgba(5,159,64,0.4)]' : step.isActive ? 'bg-[#059F40] text-white ring-2 ring-[#059F40]/30 shadow-[0_0_12px_rgba(5,159,64,0.5)]' : 'bg-stone-200 dark:bg-white/5 text-stone-400 dark:text-stone-600'}`} data-id="element-248" data-mp="30wahmqh32qdp">
                                {step.isComplete ? <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-249" data-mp="1hzuw4wsvnkup" /> : idx + 1}
                              </div>
                              <span className={`text-[10px] font-semibold tracking-wide hidden sm:block ${step.isComplete ? 'text-[#059F40] dark:text-[#22c95c]' : step.isActive ? 'text-[#059F40] dark:text-[#22c95c]' : 'text-stone-400 dark:text-stone-600'}`} data-id="element-250" data-mp="1d7tawldqejsj">
                                {step.label}
                              </span>
                            </div>
                            {idx < arr.length - 1 && <div className="flex-1 h-px mx-1.5 bg-stone-200 dark:bg-white/5 relative" data-id="element-251" data-mp="1wxx0vabnacdj">
                                <div className="absolute top-0 left-0 h-full bg-[#059F40] transition-all duration-500 shadow-[0_0_8px_rgba(5,159,64,0.4)]" style={{
                      width: step.isComplete ? '100%' : '0%'
                    }} data-id="element-252" data-mp="6kxtgk3rsx4v" />
                              </div>}
                          </Fragment>)}
                      </div>
                    </div>}
                  {/* Items */}
                  <div className="p-8" data-id="element-253" data-mp="2bjxicrqrxp7n">
                    {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-8 py-16" data-id="element-254" data-mp="2gl4rau4nkufw">
                        <div className="w-28 h-28 bg-white dark:bg-[#1a1a1a] rounded-full flex items-center justify-center border border-[#e5e5e5] dark:border-white/5 shadow-inner dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-255" data-mp="2kl5hfc146u2s">
                          <Truck className="w-12 h-12 text-brand-muted dark:text-stone-500 opacity-50" data-id="element-256" data-mp="2ucrw5icnvbaa" />
                        </div>
                        <div data-id="element-257" data-mp="34afwqowy7d0d">
                          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-258" data-mp="34b7eowxmhc86">
                            Your truck is empty
                          </h3>
                          <p className="text-brand-muted dark:text-stone-300 text-lg max-w-[300px] mx-auto" data-id="element-259" data-mp="wp7fsiqbwxmv">
                            Looks like you haven't added any materials to your
                            truck yet.
                          </p>
                        </div>
                        <div className="flex flex-col gap-3 w-full max-w-[280px]" data-id="element-260" data-mp="ipog8ebl2um6">
                          <button onClick={handleContinueShopping} className="bg-brand-charcoal dark:bg-[#1a1a1a] hover:bg-brand-charcoal/90 dark:hover:bg-white/10 text-white px-10 py-4 rounded-xl font-bold text-lg transition-colors border border-transparent dark:border-white/5 backdrop-blur-sm w-full dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-261" data-mp="1gcoje1wp065l">
                            Browse Materials
                          </button>
                          {localStorage.getItem('253gravel_last_order') && <button type="button" onClick={handleReorder} className="bg-brand-orange/10 hover:bg-brand-orange/20 text-brand-orange border border-brand-orange/30 px-10 py-4 rounded-xl font-bold text-lg transition-colors w-full" data-id="element-262" data-mp="1ucg38j9w4gri">
                              Reorder Last Order
                            </button>}
                        </div>
                      </div> : <div className="space-y-5" data-id="element-263" data-mp="14y3f5vwevozl">
                        {items.map(item => <div key={item.id} className="p-5 bg-white dark:bg-[#3b3b3b] backdrop-blur-sm rounded-2xl border border-[#e5e5e5] dark:border-white/5 shadow-sm dark:shadow-[0_4px_20px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.05)] overflow-hidden relative z-10" data-id="element-264" data-mp="19vv6anp8f72q">
                            {/* Top row: category eyebrow + remove button docked top-right */}
                            <div className="flex items-start justify-between gap-3" data-id="element-265" data-mp="1st63m2razqfj">
                              <div className="min-w-0" data-id="element-266" data-mp="2gjs8jl60gulb">
                                <p className="text-[11px] font-bold text-brand-muted dark:text-stone-400 mb-1 uppercase tracking-wider" data-id="element-267" data-mp="2gi7hi5iwnts7">
                                  {item.category}
                                </p>
                                <h4 className="font-heading font-extrabold text-lg leading-snug text-brand-charcoal dark:text-white" data-id="element-268" data-mp="4w6nsw47grdo">
                                  {item.product}
                                </h4>
                              </div>
                              <button onClick={() => removeFromCart(item.id)} className="-mt-1 -mr-1 p-2 rounded-lg text-brand-muted hover:text-brand-orange hover:bg-black/5 dark:hover:bg-white/10 transition-colors shrink-0" title="Remove item" aria-label={`Remove ${item.product} from cart`} data-id="element-269" data-mp="en9npulef0bd">
                                <Trash2 className="w-5 h-5" data-id="element-270" data-mp="3w39frprvb1oy" />
                              </button>
                            </div>

                            {/* Per-yard + rate pill */}
                            {(() => {
                    const isLight = isLightMaterial(item.product);
                    const smallLoadThreshold = 10;
                    const billableYards = item.quantity;
                    const surcharge = (() => {
                      if (billableYards < 3) return 1; // Flat $250 delivery fee covers economics
                      if (billableYards >= smallLoadThreshold) return 1;
                      return 1 + (smallLoadThreshold - billableYards) * 0.1;
                    })();
                    const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                    const fullLoadDisc = isLight ? 15 : 12;
                    const bulkPct = item.quantity >= fullLoadDisc * 4 ? 0.1 : item.quantity >= (isLight ? fullLoadDisc : fullLoadDisc * 2) ? 0.05 : 0;
                    const hasBulk = bulkPct > 0;
                    const displayPrice = hasBulk ? Math.ceil(effectivePrice * (1 - bulkPct)) : effectivePrice;
                    return <div className="mt-1 flex items-center flex-wrap gap-x-2 gap-y-1" data-id="element-271" data-mp="2xrf9jb21b616">
                                  <p className="text-sm text-brand-muted dark:text-stone-300 font-semibold" data-id="element-272" data-mp="32oyko23fr2ow">
                                    ${displayPrice}{' '}
                                    <span className="text-stone-500 dark:text-stone-400 font-medium" data-id="element-273" data-mp="2q74mr6p1i8r8">
                                      / yard
                                    </span>
                                  </p>
                                  {hasBulk && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#059F40] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-274" data-mp="2h5el781g1tfq">
                                      {Math.round(bulkPct * 100)}% Bulk Discount
                                    </span>}
                                  {!hasBulk && billableYards >= 3 && billableYards < smallLoadThreshold && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#CB6E0A] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-275" data-mp="1xiz1miu2qhqv">
                                        Small-load rate
                                      </span>}
                                  {billableYards < 3 && <span className="inline-flex items-center text-[10px] font-bold leading-none text-white bg-[#CB6E0A] px-2 py-1 rounded-md uppercase tracking-wide" data-id="element-276" data-mp="3ce738obuqlgx">
                                      Micro-load delivery
                                    </span>}
                                </div>;
                  })()}

                            {/* Bottom row: stepper (left) + line total (right) */}
                            <div className="mt-4 flex items-center justify-between gap-5" data-id="element-277" data-mp="2tn7b5e928vjf">
                              {/* Quantity stepper + caption */}
                              {(() => {
                      const isBarkMulch = isLightMaterial(item.product);
                      const fullLoad = isBarkMulch ? 15 : 12;
                      const isFullLoadMultiple = item.quantity % fullLoad === 0;
                      const loadCount = item.quantity / fullLoad;
                      const handleMinus = () => {
                        if (isFullLoadMultiple && loadCount > 1) {
                          updateQuantity(item.id, item.quantity - fullLoad);
                        } else {
                          updateQuantity(item.id, item.quantity - 1);
                        }
                      };
                      const maxQty = isBarkMulch ? 60 : 48;
                      const handlePlus = () => {
                        if (item.quantity >= maxQty) return;
                        if (isFullLoadMultiple) {
                          updateQuantity(item.id, Math.min(item.quantity + fullLoad, maxQty));
                        } else {
                          updateQuantity(item.id, Math.min(item.quantity + 1, maxQty));
                        }
                      };
                      const tonsEstimate = (item.quantity * getDensity(item.product)).toFixed(1);
                      return <div className="flex flex-col gap-1.5" data-id="element-278" data-mp="2xs1cbcld1kbw">
                                    <div className="flex items-center bg-[#f5f5f5] dark:bg-[#333333] border border-[#e5e5e5] dark:border-white/5 rounded-lg overflow-hidden w-[150px]" data-id="element-279" data-mp="2stq1ozn8r5sm">
                                      <button type="button" onClick={handleMinus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Decrease quantity" data-id="element-280" data-mp="3q5ieh0ggsj5n">
                                        <Minus className="w-5 h-5" data-id="element-281" data-mp="3g9ox93rsw1hj" />
                                      </button>
                                      <div className="flex items-center justify-center py-1.5 flex-1 whitespace-nowrap gap-1.5" data-id="element-282" data-mp="37fair3f3guzh">
                                        <span className="font-bold text-base text-brand-charcoal dark:text-white leading-none" data-id="element-283" data-mp="32feed32hj1je">
                                          {isFullLoadMultiple ? loadCount : item.quantity}
                                        </span>
                                        <span className="text-[10px] text-brand-muted dark:text-stone-300 uppercase tracking-wider font-bold" data-id="element-284" data-mp="2ogemznnl88jj">
                                          {isFullLoadMultiple ? loadCount === 1 ? 'Load' : 'Loads' : 'yd'}
                                        </span>
                                      </div>
                                      <button type="button" onClick={handlePlus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Increase quantity" data-id="element-285" data-mp="1d03oqyg8a6q2">
                                        <Plus className="w-5 h-5" data-id="element-286" data-mp="p9her1mnycdf" />
                                      </button>
                                    </div>
                                    <span className="text-xs text-stone-400 dark:text-stone-400 font-medium pl-0.5" data-id="element-287" data-mp="u7sqxj0fkyyt">
                                      {isFullLoadMultiple ? `${item.quantity} yds` : null}
                                      {isFullLoadMultiple ? ' · ' : ''}≈{' '}
                                      {tonsEstimate} tons
                                    </span>
                                  </div>;
                    })()}

                              {/* Line total */}
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const smallLoadThreshold = 10;
                      const billableYards = item.quantity;
                      const surcharge = (() => {
                        if (billableYards < 3) return 1; // Flat $250 delivery fee covers economics
                        if (billableYards >= smallLoadThreshold) return 1;
                        return 1 + (smallLoadThreshold - billableYards) * 0.1;
                      })();
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      let itemTotal = billableYards * effectivePrice;
                      const baseProduct = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
                      const aggYards = items.filter(i => baseProduct(i.product) === baseProduct(item.product)).reduce((s, i) => s + i.quantity, 0);
                      const fullLoad = isLight ? 15 : 12;
                      const bulkPct2 = aggYards >= fullLoad * 4 ? 0.1 : aggYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                      const hasBulk = bulkPct2 > 0;
                      if (hasBulk) {
                        itemTotal = Math.round(itemTotal * (1 - bulkPct2));
                      }
                      const savingsAmount = hasBulk ? Math.round(billableYards * effectivePrice * bulkPct2) : 0;
                      return <div className="flex flex-col items-end leading-none -mt-[15px] pr-1" data-id="element-288" data-mp="31mq85fgf1j5z">
                                    <p className="font-heading font-extrabold text-2xl text-brand-charcoal dark:text-white" data-id="element-289" data-mp="2ssligzd0qds1">
                                      ${itemTotal}
                                    </p>
                                    {hasBulk && savingsAmount > 0 && <span className="mt-1 text-xs text-[#059F40] font-semibold" data-id="element-290" data-mp="3l9o3yaqufjoe">
                                        ${savingsAmount} savings
                                      </span>}
                                  </div>;
                    })()}
                            </div>
                          </div>)}
                      </div>}
                  </div>

                  {/* Contractor Prompt */}
                  {(() => {
              const productYards: Record<string, number> = {};
              const baseName = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
              for (const item of items) {
                const base = baseName(item.product);
                productYards[base] = (productYards[base] || 0) + item.quantity;
              }
              return Object.values(productYards).some(y => y > 24);
            })() && <div className="mx-8 mb-2 p-4 bg-brand-orange/10 dark:bg-brand-orange/10 border border-brand-orange/30 rounded-xl flex items-start gap-3" data-id="element-291" data-mp="2z225jcedsiko">
                      <Info className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-292" data-mp="2u8qv71ad1jo0" />
                      <div data-id="element-293" data-mp="31njdnl0crisv">
                        <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-294" data-mp="31ob9mwxv3ijo">
                          Are you a contractor?
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400 mt-0.5" data-id="element-295" data-mp="3bil4claa0sxk">
                          Get priority scheduling, volume pricing, and dedicated
                          support.{' '}
                          <a href="/subcontractors" className="text-brand-orange font-bold underline hover:text-brand-orange/80 transition-colors" onClick={() => closeCart()} data-id="element-296" data-mp="3kiqaks3qhla5">
                            Sign up on our Contractors page →
                          </a>
                        </p>
                      </div>
                    </div>}

                  {/* Delivery Details */}
                  {items.length > 0 && <div className={`px-8 pb-8 pt-8 ${showCalendar ? 'relative z-[200]' : 'relative z-10'}`} data-id="element-297" data-mp="743emfak28qa">
                      <h3 className="text-2xl font-extrabold text-brand-charcoal dark:text-stone-400 tracking-tight mb-5 font-[Montserrat,_sans-serif]" data-id="element-298" data-mp="2885ma1z478z6">
                        Delivery Details
                      </h3>
                      <div className="space-y-4 bg-white dark:bg-[#3b3b3b] backdrop-blur-sm rounded-2xl border border-[#e5e5e5] dark:border-white/5 shadow-sm dark:shadow-[0_4px_20px_rgba(0,0,0,0.2),inset_0_1px_0_rgba(255,255,255,0.05)] p-5" data-id="element-299" data-mp="2hyib46dypob4">
                        {/* Address */}
                        <div data-id="element-300" data-mp="2mxbsi9wddzgk">
                          <label className="flex items-center justify-between text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-301" data-mp="2mw0frkky5hdb">
                            <span className="flex items-center" data-id="element-302" data-mp="30ugwm4ciwo96">
                              <MapPin className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-303" data-mp="2vz1gt6uygia4" />
                              Delivery Address
                            </span>
                          </label>
                          <AddressAutocomplete value={addressInput} onChange={setAddressInput} placeholder={`123 Main St, ${getRegionMeta().cities[0]}, WA`} inputClassName={`${inputClass} ${hasAttemptedSubmit && !isAddressValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required variant="light" data-id="element-304" data-mp="11ljrpfewjdcz" />
                          <div aria-live="polite" className="mt-1.5" data-id="element-305" data-mp="34co4fa3n3yt3">
                            {isCalculatingDelivery && <Skeleton className="h-5 w-[60%] rounded" data-id="element-306" data-mp="38ej121amlo0j" />}
                            {addressError && !isCalculatingDelivery && <p className="text-sm text-red-500 font-medium" data-id="element-307" data-mp="2uixhwar2pyg1">
                                {addressError}
                              </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-sm text-green-600 dark:text-green-400 flex flex-wrap items-center gap-x-1.5 font-medium" data-id="element-308" data-mp="2pij8ao9tqgfw">
                                  <CheckCircle2 className="w-4 h-4 shrink-0" data-id="element-309" data-mp="jmf5patq7ejq" />
                                  <span data-id="element-310" data-mp="nplbyigu95q7">
                                    Zone {deliveryZone}{' '}
                                    <span className="font-bold" data-id="element-311" data-mp="37lmaaky7co8j">
                                      {deliveryFee === 0 ? '(Free)' : deliveryCount > 1 ? `(+${totalDeliveryFee.toFixed(0)} · ${deliveryCount} loads)` : `(+${deliveryFee})`}
                                    </span>
                                  </span>
                                  <a href={deliveryZonesUrl(addressInput)} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300 transition-colors whitespace-nowrap" data-id="element-312" data-mp="2svf4b4pdsxoq">
                                    View zones
                                  </a>
                                </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-sm text-brand-orange flex items-center font-medium" data-id="element-313" data-mp="2a0rqhhndzpn2">
                                  <Info className="w-4 h-4 mr-1" data-id="element-314" data-mp="d3r8xdq0nd81" />
                                  Outside standard zones. We'll call with a
                                  custom quote.
                                </p>}
                          </div>
                        </div>

                        {/* Name */}
                        <div data-id="element-315" data-mp="pgmihzni1nc0">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-316" data-mp="phnxgf7mytz0">
                            <FileText className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-317" data-mp="uhk1ufk8wnf3" />
                            Full Name
                          </label>
                          <input ref={nameRef} type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && isNameValid) {
                      e.preventDefault();
                      setTimeout(() => phoneRef.current?.focus(), 100);
                    }
                  }} placeholder="John Doe" autoComplete="name" className={`${inputClass} ${hasAttemptedSubmit && !isNameValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && !isNameValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={hasAttemptedSubmit && !isNameValid} data-id="element-318" data-mp="1rjw6ehx76b6" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-319" data-mp="38r40jw2gc5dy">
                          <div data-id="element-320" data-mp="3dn1fpz1zb5p5">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-321" data-mp="2g02jtqj421sb">
                              <Phone className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-322" data-mp="2l08dfc8xxyd1" />
                              Phone Number
                            </label>
                            <input ref={phoneRef} type="tel" value={phone} onChange={e => {
                      const formatted = formatPhone(e.target.value);
                      setPhone(formatted);
                      if (formatted.length === 14) {
                        setTimeout(() => emailRef.current?.focus(), 100);
                      }
                    }} placeholder="(206) 555-1234" autoComplete="tel" className={`${inputClass} ${hasAttemptedSubmit && !isPhoneValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && !isPhoneValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={!isPhoneValid && phone.length > 0 || hasAttemptedSubmit && !isPhoneValid} data-id="element-323" data-mp="2u0d8cmwy74ab" />
                          </div>
                          <div data-id="element-324" data-mp="1622rho7shbl1">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-325" data-mp="161t6yg5ijwmq">
                              <Mail className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-326" data-mp="120r9o4v5pune" />
                              Email Address
                            </label>
                            <input ref={emailRef} type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && isEmailValid) {
                        e.preventDefault();
                        setShowCalendar(true);
                        setTimeout(() => dateBtnRef.current?.focus(), 100);
                      }
                    }} placeholder="you@example.com" autoComplete="email" className={`${inputClass} ${hasAttemptedSubmit && !isEmailValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && !isEmailValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} required aria-required="true" aria-invalid={!isEmailValid && email.length > 0 || hasAttemptedSubmit && !isEmailValid} data-id="element-327" data-mp="2wd7bmtgx6mmg" />
                          </div>
                        </div>

                        {/* Date & Time Row */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-328" data-mp="3p7djy3aowdf0">
                          <div className={`relative ${showCalendar ? 'z-[100]' : ''}`} data-id="element-329" data-mp="28uf940pp3ash">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-330" data-mp="2dub4jbkbttwf">
                              <CalendarDays className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-331" data-mp="268xpovy9wo4z" />
                              Date
                            </label>
                            <button ref={dateBtnRef} type="button" onClick={() => setShowCalendar(!showCalendar)} className={`${inputClass} text-left flex items-center justify-between w-full ${hasAttemptedSubmit && !isDateValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid ? 'ring-2 ring-[#059F40] border-[#059F40]' : deliveryDate ? 'border-[#059F40] dark:border-[#059F40]' : ''}`} data-id="element-332" data-mp="2fb72xtkxevm2">
                              <span className={deliveryDate ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-333" data-mp="1wdxjicflpi4t">
                                {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        }) : 'Select'}
                              </span>
                              <CalendarDays className="w-5 h-5 text-stone-300" data-id="element-334" data-mp="1chbbzichx1so" />
                            </button>
                            {/* Calendar Dropdown */}
                            <AnimatePresence data-id="element-335" data-mp="1mg0sp7snog7c">
                              {showCalendar && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-[320px] bg-white dark:bg-[#222222] backdrop-blur-xl border border-[#e5e5e5] dark:border-white/5 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-5" data-id="element-336" data-mp="1r8tylyzv1ul0">
                                  {(() => {
                          const today = new Date();
                          const calMonth = calendarMonth || new Date(today.getFullYear(), today.getMonth(), 1);
                          const year = calMonth.getFullYear();
                          const month = calMonth.getMonth();
                          const firstDay = new Date(year, month, 1).getDay();
                          const daysInMonth = new Date(year, month + 1, 0, 0).getDate();
                          const monthName = calMonth.toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric'
                          });
                          const days = [];
                          for (let i = 0; i < firstDay; i++) days.push(null);
                          for (let d = 1; d <= daysInMonth; d++) days.push(d);
                          return <>
                                        <div className="flex items-center justify-between mb-4" data-id="element-337" data-mp="1rk2xea98m78w">
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-338" data-mp="1mpffwvcnid3t">
                                            <ChevronLeft className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-339" data-mp="1f3ts2yxlim6g" />
                                          </button>
                                          <span className="text-base font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-340" data-mp="1k1d9g7plbf2m">
                                            {monthName}
                                          </span>
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-341" data-mp="wdc6deb6kqul">
                                            <ChevronRight className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-342" data-mp="memklag5ym92" />
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-343" data-mp="7rnvvn8oy5y1">
                                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <span key={d} className="text-xs font-bold text-stone-500 dark:text-stone-400 py-1 uppercase" data-id="element-344" data-mp="2vh18qlqwgve">
                                              {d}
                                            </span>)}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1" data-id="element-345" data-mp="qpid2ojz3nri">
                                          {days.map((day, i) => {
                                if (day === null) return <span key={`empty-${i}`} data-id="element-346" data-mp="10heby1tvvhws" />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const isPast = dateStr < minDate;
                                const isSelected = dateStr === deliveryDate;
                                const isToday = dateStr === today.toISOString().split('T')[0];
                                const cellDow = new Date(dateStr + 'T12:00:00').getDay();
                                const isSat = cellDow === 6;
                                // Dispatch is closed on
                                // Sundays — block the
                                // entire day at the picker
                                // so customers don't pick
                                // a date, see every time
                                // window labeled CLOSED,
                                // and walk away frustrated.
                                const isSun = cellDow === 0;
                                const isDisabled = isPast || isSun;
                                return <button key={dateStr} type="button" disabled={isDisabled} onClick={() => {
                                  setDeliveryDate(dateStr);
                                  setShowCalendar(false);
                                  setTimeout(() => {
                                    timeBtnRef.current?.focus();
                                    setShowTimeDropdown(true);
                                  }, 100);
                                }} title={isSun ? 'Closed Sundays — please choose another day' : isSat && !isPast ? 'Saturday delivery: +$40 weekend surcharge' : undefined} className={`relative text-base font-bold py-2 rounded-xl transition-colors ${isDisabled ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed line-through decoration-stone-300 dark:decoration-stone-600/60' : isSelected ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : isToday ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-347" data-mp="26wvta65ita4v" data-mpi={i}>
                                                {day}
                                                {isSat && !isPast && <span className={`absolute -top-0.5 -right-0.5 text-[8px] font-extrabold leading-none px-1 py-0.5 rounded ${isSelected ? 'bg-white/30 text-white' : 'bg-brand-orange/20 text-brand-orange'}`} data-id="element-348" data-mp="7afa8k1m8f9">
                                                    +$40
                                                  </span>}
                                              </button>;
                              })}
                                        </div>
                                      </>;
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                          <div className={`relative ${showTimeDropdown ? 'z-[100]' : ''}`} data-id="element-349" data-mp="2hjzov8j4dai9">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-350" data-mp="2wcjmxurewrsu">
                              <Clock className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-351" data-mp="23tw3skvz7mue" />
                              Time
                            </label>
                            <input type="hidden" value={deliveryTime} required data-id="element-352" data-mp="1yti69ws03vam" />
                            <button ref={timeBtnRef} type="button" onClick={() => setShowTimeDropdown(!showTimeDropdown)} className={`${inputClass} text-left flex items-center justify-between w-full ${hasAttemptedSubmit && !deliveryTime ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime ? 'ring-2 ring-[#059F40] border-[#059F40]' : deliveryTime ? 'border-[#059F40] dark:border-[#059F40]' : ''}`} data-id="element-353" data-mp="ebzairpy8wo4">
                              <span className={deliveryTime ? 'text-stone-900 dark:text-white flex items-center gap-2 min-w-0' : 'text-stone-400'} data-id="element-354" data-mp="122ctlsqk3l6k">
                                <span className="truncate" data-id="element-355" data-mp="t1y74q1uw01h">
                                  {deliveryTime === 'morning' ? 'Morning (7–11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12–4 PM)' : deliveryTime === 'flexible' ? 'Flexible' : 'Select'}
                                </span>
                              </span>
                              {/* Right-side Clock icon. When the user has
                               picked the 7–11 AM window on a same-day order,
                               the Clock glows amber and becomes its own tap
                               target that opens the "not guaranteed"
                               explanation popover (stopPropagation so the
                               outer time-selector button doesn't toggle the
                               dropdown). Otherwise it's just the field's
                               passive affordance. */}
                              {(() => {
                        const d = new Date();
                        const y = d.getFullYear();
                        const m = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        const todayStr = `${y}-${m}-${day}`;
                        const isMorningToday = deliveryTime === 'morning' && deliveryDate === todayStr;
                        if (isMorningToday) {
                          return <span role="button" tabIndex={0} aria-label="Same-day morning is not guaranteed. Tap for details." onClick={e => {
                            e.stopPropagation();
                            e.preventDefault();
                            setShowNotGuaranteedInfo(v => !v);
                          }} onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.stopPropagation();
                              e.preventDefault();
                              setShowNotGuaranteedInfo(v => !v);
                            }
                          }} className="shrink-0 inline-flex items-center justify-center rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#DD4132]/60" data-id="element-356" data-mp="7qrilmzgm2st">
                                      <Clock className="w-5 h-5 text-[#DD4132] dark:text-[#DD4132] shrink-0" style={{
                              filter: 'drop-shadow(0 0 6px rgba(221,65,50,0.6))'
                            }} data-id="element-357" data-mp="197qryxruo3j6" />
                                    </span>;
                        }
                        return <Clock className="w-5 h-5 text-stone-300 shrink-0" data-id="element-358" data-mp="1gsayi7zaqbqd" />;
                      })()}
                            </button>
                            <AnimatePresence data-id="element-359" data-mp="1cowgjbrsrlaa">
                              {showTimeDropdown && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 right-0 w-[320px] max-w-[calc(100vw-3rem)] bg-white dark:bg-[#222222] backdrop-blur-xl border border-[#e5e5e5] dark:border-white/5 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-3 space-y-1" data-id="element-360" data-mp="1qoxher82aspr">
                                  {(() => {
                          // Gate windows by the selected day's dispatch hours.
                          // Mon–Fri 6a–5p, Sat ends at 1 PM, Sun closed.
                          const dow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
                          const isSat = dow === 6;
                          const isSun = dow === 0;
                          const noDate = !deliveryDate;
                          // Detect same-day: morning windows can't be
                          // guaranteed since trucks are already routed.
                          const todayStr = (() => {
                            const d = new Date();
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${y}-${m}-${day}`;
                          })();
                          const isSameDay = deliveryDate === todayStr;
                          // After 10 AM the morning window
                          // (7–11 AM) is physically impossible
                          // for same-day — by then dispatch is
                          // halfway through the morning route
                          // and there's no truck left that can
                          // still hit a 7–11 slot today. Block
                          // the option entirely so customers
                          // don't pick it and then have to be
                          // told no.
                          const currentHour = new Date().getHours();
                          const morningTooLate = isSameDay && currentHour >= 10;
                          const opts = [{
                            value: 'morning',
                            label: morningTooLate ? 'Morning — too late for today' : 'Morning (7–11 AM)',
                            disabled: isSun || morningTooLate,
                            note: isSameDay && !morningTooLate ? 'Best effort — not guaranteed' : null
                          }, {
                            value: 'afternoon',
                            // Saturday: dispatch ends at 1 PM, so afternoon is unavailable
                            label: isSat ? 'Afternoon — Sat ends 1 PM' : 'Afternoon (12–4 PM)',
                            disabled: isSun || isSat,
                            note: isSameDay && !isSat ? 'Recommended for same-day' : null
                          }, {
                            value: 'flexible',
                            label: isSat ? 'Flexible (before 1 PM)' : 'Flexible',
                            disabled: isSun,
                            note: isSameDay ? 'Best chance same-day' : null
                          }];
                          if (noDate) {
                            return <p className="text-xs text-stone-700 dark:text-stone-300 px-3 py-3 font-medium" data-id="element-361" data-mp="4i0hnr8hc5f6">
                                          Pick a date first.
                                        </p>;
                          }
                          return opts.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
                            if (opt.disabled) return;
                            setDeliveryTime(opt.value);
                            setShowTimeDropdown(false);
                            // Continue the auto-advance
                            // chain — once a time window
                            // is picked, take the customer
                            // straight to the delivery
                            // instructions field (the last
                            // required field). Align the whole
                            // section to the top so the label,
                            // textarea, and the inline "Done —
                            // Continue" button all sit above the
                            // mobile keyboard.
                            setTimeout(() => {
                              instructionsSectionRef.current?.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                              instructionsRef.current?.focus({
                                preventScroll: true
                              });
                            }, 200);
                          }} className={`w-full text-right px-4 py-3 rounded-xl text-base font-bold transition-colors flex items-center justify-between flex-row-reverse ${opt.disabled ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed' : deliveryTime === opt.value ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-362" data-mp="3qh4ywshwmyzi">
                                        <div className="flex flex-col items-end" data-id="element-363" data-mp="3socl7pw208u3">
                                          <span data-id="element-364" data-mp="3sm9pqqc4tnhz">{opt.label}</span>
                                          {opt.note && !opt.disabled && <span className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${opt.value === 'morning' ? deliveryTime === opt.value ? 'text-white/85' : 'text-[#DD4132] dark:text-[#DD4132]' : deliveryTime === opt.value ? 'text-white/85' : 'text-green-600 dark:text-green-400'}`} data-id="element-365" data-mp="1b9n7zfyjtyf">
                                              {opt.note}
                                            </span>}
                                        </div>
                                        {opt.disabled && <span className="text-[10px] uppercase tracking-wider font-bold text-[#DD4132]" data-id="element-366" data-mp="6am6ka7meiw9">
                                            Closed
                                          </span>}
                                      </button>);
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                            {/* Detailed "why isn't same-day morning guaranteed?"
                             popover — opens when the chip in the time pill
                             is clicked. Anchored just below the pill so the
                             customer can read the explanation without
                             losing context. A semi-transparent backdrop
                             catches outside clicks to dismiss. */}
                            <AnimatePresence data-id="element-367" data-mp="1zricyb9j8c52">
                              {showNotGuaranteedInfo && <>
                                  {/* Invisible click-outside catcher */}
                                  <div className="fixed inset-0 z-[110]" onClick={() => setShowNotGuaranteedInfo(false)} data-id="element-368" data-mp="29per7bxrnn7e" />
                                  <motion.div initial={{
                          opacity: 0,
                          y: -6,
                          scale: 0.98
                        }} animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1
                        }} exit={{
                          opacity: 0,
                          y: -6,
                          scale: 0.98
                        }} transition={{
                          duration: 0.18
                        }} role="dialog" aria-modal="true" aria-labelledby="not-guaranteed-title" className="absolute z-[120] top-full mt-2 right-0 w-[340px] max-w-[calc(100vw-3rem)] bg-white dark:bg-[#222222] backdrop-blur-xl border border-[#DD4132]/40 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] px-6 py-5" data-id="element-369" data-mp="1lzapja451xj1">
                                    <div className="flex items-start gap-3 mb-3" data-id="element-370" data-mp="373huej4e96im">
                                      <div className="w-9 h-9 rounded-full bg-[#DD4132]/15 border border-[#DD4132]/40 flex items-center justify-center shrink-0" data-id="element-371" data-mp="3c5gwzqsb7zei">
                                        <Info className="w-4 h-4 text-[#DD4132] dark:text-[#DD4132]" data-id="element-372" data-mp="rd59faszbs20" />
                                      </div>
                                      <div className="flex-1 min-w-0" data-id="element-373" data-mp="11gtsiq14l5dy">
                                        <h4 id="not-guaranteed-title" className="text-sm font-extrabold text-stone-900 dark:text-white leading-tight" data-id="element-374" data-mp="wjjx8tr21zi7">
                                          Why isn't same-day morning guaranteed?
                                        </h4>
                                        <p className="text-[11px] font-bold uppercase tracking-wider text-[#DD4132] dark:text-[#DD4132] mt-0.5" data-id="element-375" data-mp="irn8mzoli7qk">
                                          7–11 AM same-day only
                                        </p>
                                      </div>
                                      <button type="button" onClick={() => setShowNotGuaranteedInfo(false)} aria-label="Close" className="shrink-0 -mt-1 -mr-1 p-1.5 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors" data-id="element-376" data-mp="4rujv9wmq1l8">
                                        <X className="w-4 h-4" data-id="element-377" data-mp="268adpvzwadeh" />
                                      </button>
                                    </div>

                                    <div className="space-y-3 text-sm text-stone-700 dark:text-stone-300 leading-relaxed" data-id="element-378" data-mp="2b5ao2j3s22be">
                                      <p data-id="element-379" data-mp="2197bejbaula7">
                                        Our morning trucks are dispatched
                                        starting at 6 AM, and most are already
                                        loaded and routed for previously booked
                                        deliveries before the day even starts.
                                      </p>
                                      <p data-id="element-380" data-mp="1iipv5e7nli5q">
                                        When you book a morning slot{' '}
                                        <span className="font-bold" data-id="element-381" data-mp="14iyaiummt3ir">
                                          on the same day
                                        </span>
                                        , we squeeze you into open gaps if we
                                        can — but we can't promise the window
                                        because the schedule is already in
                                        motion.
                                      </p>
                                    </div>

                                    <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-382" data-mp="uu18017vt2nf">
                                      <button type="button" onClick={() => {
                              setDeliveryTime('flexible');
                              setShowNotGuaranteedInfo(false);
                              // Match the main time-picker:
                              // hand the customer off to the
                              // instructions field once a
                              // time choice is made, aligning the
                              // section to the top so its label,
                              // field, and "Done — Continue" button
                              // are all visible above the keyboard.
                              setTimeout(() => {
                                instructionsSectionRef.current?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'start'
                                });
                                instructionsRef.current?.focus({
                                  preventScroll: true
                                });
                              }, 200);
                            }} className="px-3 py-2 rounded-lg bg-[#059F40] hover:bg-[#17A347] text-white text-xs font-bold transition-colors" data-id="element-383" data-mp="18u2h23yavgdz">
                                        Switch to Flexible
                                      </button>
                                      <a href="tel:206-619-3742" className="px-3 py-2 rounded-lg border border-stone-300 dark:border-white/5 bg-white/60 dark:bg-[#1a1a1a] hover:bg-white dark:hover:bg-white/10 text-stone-800 dark:text-white text-xs font-bold transition-colors text-center flex items-center justify-center gap-1.5" data-id="element-384" data-mp="3epo3hkicf39q">
                                        <Phone className="w-3.5 h-3.5" data-id="element-385" data-mp="36fyrzixtje15" />
                                        Call to confirm
                                      </a>
                                    </div>
                                  </motion.div>
                                </>}
                            </AnimatePresence>
                          </div>
                        </div>

                        {/* Same-day morning warning is now displayed inline
                         inside the time selector pill itself — see the
                         "Not guaranteed" chip rendered next to "Morning
                         (7–11 AM)" above. The standalone amber banner that
                         used to sit here was removed to declutter the
                         checkout form. */}

                        {/* Removed: standalone "Delivering tomorrow!"
                         countdown pill and the dismissible "You don't
                         need to be home" green card. Both
                         added clutter without enough informational
                         value — the date field already communicates
                         when delivery lands, and the dump-location
                         picker directly below makes the no-home-needed
                         workflow self-evident. Customer-feedback driven
                         decluttering pass. */}

                        {/* Delivery Instructions (required) — single
                         free-text field that replaces both the older
                         Dump Location dropdown and the Additional Notes
                         textarea. Customers describe the drop spot and
                         any access notes in their own words, and the
                         inline reassurance row above reminds them they
                         don't need to be home — the most common
                         checkout hesitation.
                         Drop-site photo lives on the SUCCESS screen
                         post-submit so the form stays short. */}
                        <div ref={instructionsSectionRef} className="scroll-mt-4" data-id="element-386" data-mp="38nb09j39qwss">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2 font-[Inter,_sans-serif]" data-id="element-387" data-mp="34kxuwvsfp0wj">
                            <MapPin className="w-5 h-5 mr-2 text-stone-500 dark:text-stone-400" data-id="element-388" data-mp="2gw56rli1zxch" />
                            Delivery Instructions
                            <span className="ml-1.5 text-amber-500 dark:text-amber-400" aria-hidden="true" data-id="element-389" data-mp="2px33diq5x2l1">
                              *
                            </span>
                            <span className="sr-only" data-id="element-390" data-mp="56ccaxwpio91">required</span>
                          </label>

                          {/* "You don't need to be home" reassurance — softer
                           treatment than the old dismissible card. Lives
                           inline above the textarea so the message lands
                           right where the customer is wondering whether
                           they need to be there to direct the driver. */}
                          <div className="flex items-start gap-2 mb-2.5 text-xs text-brand-muted dark:text-stone-400 leading-relaxed" data-id="element-391" data-mp="5v8h5k8twzc0">
                            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#059F40] dark:text-[#22c95c]" strokeWidth={3} data-id="element-392" data-mp="yhjpxi6la6jw" />
                            <p data-id="element-393" data-mp="tjrr047j1k67">
                              <span className="font-bold dark:text-stone-200 text-[#E6AE60]" data-id="element-394" data-mp="tktej85qbz4t">
                                You don't need to be home.
                              </span>{' '}
                              Just tell the driver where to dump the load and
                              note any access details — driveway side, gate
                              codes, dogs to avoid, etc.
                            </p>
                          </div>

                          <textarea ref={instructionsRef} value={instructions} onChange={e => setInstructions(e.target.value)} onFocus={() => setInstructionsFocused(true)} onBlur={() =>
                  // Delay so a tap on the inline "Done" button
                  // (which steals focus) still registers before we
                  // hide it.
                  setTimeout(() => setInstructionsFocused(false), 120)} placeholder="e.g. Left side of driveway near the fence. Gate code 1234. Friendly dog in backyard." rows={4} className={`${inputClass} resize-none ${hasAttemptedSubmit && !instructions.trim() ? 'ring-2 ring-[#059F40] border-[#059F40]' : isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !!deliveryTime && !instructions.trim() ? 'ring-2 ring-[#059F40] border-[#059F40]' : ''}`} aria-required="true" aria-invalid={hasAttemptedSubmit && !instructions.trim()} data-id="element-395" data-mp="1ju0ufd2dxive" />

                          {/* Mobile keyboard escape hatch — while the textarea
                           is focused (keyboard open, covering the lower half of
                           the screen and the checkout CTA), show a button that
                           dismisses the keyboard and scrolls straight down to
                           "Proceed to Checkout". Hidden on md+ where the
                           keyboard isn't an issue. */}
                          {/* Mobile keyboard escape hatch — a small "Next"
                           button docked lower-right (replaces the old
                           full-width green pill). Dismisses the keyboard and
                           scrolls to the checkout CTA. */}
                          {instructionsFocused && <div className="md:hidden mt-3 flex justify-end" data-id="element-396" data-mp="1f29vymqb5rzc">
                              <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => {
                      instructionsRef.current?.blur();
                      setInstructionsFocused(false);
                      // Wait for the keyboard to collapse before
                      // scrolling so the CTA lands in view.
                      setTimeout(() => {
                        checkoutCtaRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center'
                        });
                      }, 180);
                    }} className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-[#059F40] hover:bg-[#17A347] text-white font-bold text-sm transition-colors" data-id="element-397" data-mp="1f05o5tpag8ts">
                                Next
                                <ArrowRight className="w-4 h-4" data-id="element-398" data-mp="2e0r4tsjxhueo" />
                              </button>
                            </div>}
                        </div>
                      </div>
                    </div>}

                  {/* Summary */}
                  {items.length > 0 && <div className={`px-8 pb-8 border-t border-white/20 dark:border-white/5 pt-8 bg-gradient-to-t from-white/50 to-transparent dark:from-white/5 dark:to-transparent ${showCalendar ? 'relative z-[50]' : ''}`} data-id="element-399" data-mp="ca5x2ir7hsb0">
                      <div className="space-y-3 mb-6" data-id="element-400" data-mp="28cj47jq65xa2">
                        {/* Materials row — matches the checkout summary: the
                         name ("Materials") sits on its own line at 18px bold
                         with the price, and the quantity / tons detail drops
                         below at 12px normal weight. */}
                        <div className="flex justify-between items-start gap-3" data-id="element-401" data-mp="1udt3lu455fgy">
                          <div className="flex flex-col min-w-0" data-id="element-402" data-mp="1plrlvso7ewjj">
                            <span className="text-[18px] font-bold text-brand-charcoal dark:text-white" data-id="element-403" data-mp="1kmy7ly13f1ib">
                              Materials
                            </span>
                            <span className="text-[12px] font-normal text-brand-charcoal dark:text-white" data-id="element-404" data-mp="1flsdpwvp5877">
                              {materialYards} yards
                              {yardsBilledExtra > 0 && <span data-id="element-405" data-mp="16np3hwq5vxup">
                                  {' '}
                                  · billed as {totalYards} yd (3 yd order min)
                                </span>}{' '}
                              · ≈{' '}
                              {items.reduce((sum, item) => sum + (item.requestedQuantity ?? item.quantity) * getDensity(item.product), 0).toFixed(1)}{' '}
                              tons
                              {deliveryCount > 1 ? ` · ${deliveryCount} loads` : ''}
                            </span>
                          </div>
                          <span className="shrink-0 text-[18px] font-bold text-brand-charcoal dark:text-white" data-id="element-406" data-mp="33mnydq4prqwv">
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>

                        {/* Promo Code — shown as a plain breakdown row (no pill)
                         so it reads as part of the running total, matching the
                         Delivery Fee / Sales Tax rows. */}
                        <div className="py-1" data-id="element-407" data-mp="2kssbh0ldfsf5">
                          {promo.code ? <div className="flex justify-between items-center" data-id="element-408" data-mp="3o4hge5181hyj">
                              <span className="flex items-center gap-1.5 text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-409" data-mp="3j5whc8xph4bo">
                                Promo {promo.code}
                                <button type="button" onClick={() => {
                        clearPromoCode();
                        setPromoInput('');
                        try {
                          sessionStorage.removeItem('promo_hint');
                        } catch {}
                      }} className="text-xs font-medium text-brand-charcoal/70 dark:text-white/60 hover:text-brand-charcoal dark:hover:text-white underline ml-1" data-id="element-410" data-mp="3swiorfv9qoja">
                                  Remove
                                </button>
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-411" data-mp="oikkv1objcvb">
                                −${promo.discount.toFixed(2)}
                              </span>
                            </div> : !showPromoInput ? <button type="button" onClick={() => setShowPromoInput(true)} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors underline" data-id="element-412" data-mp="111es1209ddvi">
                              Have a promo code?
                            </button> : <div className="space-y-1.5" data-id="element-413" data-mp="s2ba41ctv4il">
                              <div className="flex gap-2" data-id="element-414" data-mp="1frdo8z896f8c">
                                <input type="text" value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const ok = applyPromoCode(promoInput);
                          if (ok) {
                            try {
                              sessionStorage.removeItem('promo_hint');
                            } catch {}
                          }
                        }
                      }} placeholder="Enter code (e.g. FIRST10)" className="flex-1 px-3 py-2 bg-white dark:bg-[#1a1a1a] border border-[#e5e5e5] dark:border-white/5 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 text-sm font-bold uppercase tracking-wider focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 focus:outline-none" data-id="element-415" data-mp="1kow130pe5elm" />
                                <button type="button" onClick={() => {
                        const ok = applyPromoCode(promoInput);
                        if (ok) {
                          try {
                            sessionStorage.removeItem('promo_hint');
                          } catch {}
                        }
                      }} className="px-4 py-2 bg-brand-charcoal dark:bg-[#1a1a1a] hover:bg-brand-charcoal/90 dark:hover:bg-white/10 text-white rounded-lg font-bold text-sm border border-transparent dark:border-white/5 transition-colors" data-id="element-416" data-mp="mbzw3dz6y1dl">
                                  Apply
                                </button>
                              </div>
                              {promo.error && <p className="text-xs text-red-500 dark:text-red-400 font-medium px-1" data-id="element-417" data-mp="1dj8i6nltnhap">
                                  {promo.error}
                                </p>}
                            </div>}
                        </div>

                        {deliveryCount > 1 && <div className="flex items-start gap-2 p-4 bg-brand-orange/10 border border-brand-orange/20 rounded-lg backdrop-blur-sm" data-id="element-418" data-mp="1mdwngdrecyzi">
                            <Info className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-419" data-mp="1w96pv2lpj8dt" />
                            <p className="text-sm text-stone-700 dark:text-stone-300 font-medium" data-id="element-420" data-mp="2192soyio4trs">
                              Your order requires {deliveryCount} separate
                              deliveries. Each delivery has its own delivery
                              fee.
                            </p>
                          </div>}

                        {/* Delivery Fee Row */}
                        {(() => {
                  if (isCalculatingDelivery) {
                    return <div className="flex justify-between items-center" data-id="element-421" data-mp="3hsmgg4mm31jz">
                                <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-422" data-mp="3vs49iw6xx0z5">
                                  Delivery Fee
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-423" data-mp="3qvdtg8v4y5dq">
                                  <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-424" data-mp="4luk0rb7eu07" />
                                </span>
                              </div>;
                  }
                  if (deliveryZone === null && addressInput) {
                    return <div className="flex justify-between items-center" data-id="element-425" data-mp="ec5wx3cv8dj8">
                                <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-426" data-mp="scgne5a9jq3v">
                                  Delivery Fee
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-427" data-mp="16z5mw74im3a9">
                                  <span className="text-brand-orange text-sm font-bold" data-id="element-428" data-mp="1bvw2ktceqwv5">
                                    Custom Quote
                                  </span>
                                </span>
                              </div>;
                  }
                  if (deliveryFee === 0 && addressInput) {
                    return <div className="flex justify-between items-center" data-id="element-429" data-mp="o5jk4ojjdhqb">
                                <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-430" data-mp="szw5530nv146">
                                  Delivery Fee
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-431" data-mp="j91hpvbohvh5">
                                  <span className="text-green-500 font-extrabold" data-id="element-432" data-mp="59jzjysw1ggr">
                                    FREE
                                  </span>
                                </span>
                              </div>;
                  }
                  if (deliveryFee > 0) {
                    const microItems = items.filter(item => item.quantity > 0 && item.quantity < 3);
                    const microCount = microItems.length;
                    // Count standard truckLOADS, not line items — a
                    // single item can span multiple loads (e.g. 24 yd
                    // = two 12 yd loads), each billed the zone fee.
                    const standardCount = items.filter(item => item.quantity >= 3).reduce((sum, item) => {
                      const n = item.product.toLowerCase();
                      const isLight = n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('play chips');
                      const fullLoad = isLight ? 15 : 12;
                      return sum + Math.ceil(item.quantity / fullLoad);
                    }, 0);
                    const standardTotal = standardCount * deliveryFee;
                    const microTotal = microCount * 250;
                    return <div className="space-y-3" data-id="element-433" data-mp="3l544xokbncqg">
                                {standardCount > 0 && <div className="flex justify-between items-start gap-3" data-id="element-434" data-mp="3q58o5mcm6lkd">
                                    <span className="text-lg font-extrabold text-brand-charcoal dark:text-white min-w-0" data-id="element-435" data-mp="3uvp1l6gfq76q">
                                      Delivery Fee
                                      {deliveryZone !== null && <span className="text-xs font-medium text-brand-charcoal dark:text-white ml-1.5" data-id="element-436" data-mp="8ogzcd0tagex">
                                          · Zone {deliveryZone}
                                        </span>}
                                    </span>
                                    <span className="flex flex-col items-end shrink-0 text-right" data-id="element-437" data-mp="hn2quxo9h5ru">
                                      <span className="font-extrabold text-lg text-brand-charcoal dark:text-white whitespace-nowrap" data-id="element-438" data-mp="2ejjqhkvzetsu">
                                        ${standardTotal.toFixed(2)}
                                      </span>
                                      {standardCount > 1 && <span className="text-sm text-stone-400 whitespace-nowrap" data-id="element-439" data-mp="1j6h5re7gzkvn">
                                          ({standardCount} × $
                                          {deliveryFee.toFixed(2)})
                                        </span>}
                                    </span>
                                  </div>}
                                {microCount > 0 && <div className="flex justify-between items-start" data-id="element-440" data-mp="10hatoa16lrit">
                                    <div className="flex flex-col" data-id="element-441" data-mp="1f5f7azldncuq">
                                      <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-442" data-mp="1f7rxg01agtiv">
                                        Micro-load delivery
                                      </span>
                                      <span className="text-xs font-medium text-brand-charcoal dark:text-white mt-0.5" data-id="element-443" data-mp="hqb4b7pryfp0">
                                        {microItems[0].quantity} yd load · flat
                                        fee (under 3 yd)
                                      </span>
                                    </div>
                                    <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-444" data-mp="2jgrzdmyj05b3">
                                      ${microTotal.toFixed(2)}
                                      {microCount > 1 && <span className="text-sm text-stone-400 ml-1.5" data-id="element-445" data-mp="3c5fqw09vbqx3">
                                          ({microCount} × $250.00)
                                        </span>}
                                    </span>
                                  </div>}
                              </div>;
                  }
                  return <div className="flex justify-between items-center" data-id="element-446" data-mp="3774fhl3xd8cr">
                              <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-447" data-mp="1rmpzdcned8uq">
                                Delivery Fee
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-448" data-mp="25ufzkmngz4l">
                                <span className="text-stone-500 text-sm font-medium" data-id="element-449" data-mp="3tdysd9d7x8a9">
                                  Enter address above
                                </span>
                              </span>
                            </div>;
                })()}

                        {/* Saturday Surcharge Row */}
                        {saturdaySurcharge > 0 && <div className="flex justify-between items-center" data-id="element-450" data-mp="3an8nxg801dfa">
                            <span className="text-base font-bold text-[#DD4132] dark:text-[#DD4132]" data-id="element-451" data-mp="3apbo2t2v8mxq">
                              Saturday Delivery
                              <span className="text-xs font-medium text-[#DD4132]/80 dark:text-[#DD4132]/80 ml-1" data-id="element-452" data-mp="30puiebnm691r">
                                (weekend surcharge)
                              </span>
                            </span>
                            <span className="font-extrabold text-lg text-[#DD4132] dark:text-[#DD4132]" data-id="element-453" data-mp="yvbv0q7ivxiy">
                              ${saturdaySurcharge.toFixed(2)}
                            </span>
                          </div>}

                        {/* Sales Tax Row — WA combined ~10.1% on materials */}
                        <div className="flex justify-between items-center" data-id="element-454" data-mp="1l5txx3n1y1qf">
                          <span className="text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-455" data-mp="1l7y09h5aysol">
                            Sales Tax
                            <span className="text-xs font-medium text-brand-charcoal dark:text-white ml-1" data-id="element-456" data-mp="sgmqff5me1t1">
                              (est. {(TAX_RATE * 100).toFixed(1)}%)
                            </span>
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-457" data-mp="12gx0iqwcyt3z">
                            ${estimatedTax.toFixed(2)}
                          </span>
                        </div>

                        <div className="mt-4 pt-4 border-t border-white/20 dark:border-white/5 flex justify-between items-end" data-id="element-458" data-mp="2dwbzu1c1hx29">
                          <div className="flex flex-col" data-id="element-459" data-mp="2uxht98why6h9">
                            <span className="text-xs font-bold text-[#059F40] dark:text-[#22c95c] uppercase tracking-wider mb-1" data-id="element-460" data-mp="2uuva0c08u2j7">
                              You pay on delivery
                            </span>
                            <span className="text-brand-charcoal dark:text-white font-extrabold text-xl" data-id="element-461" data-mp="33vteswiiae2u">
                              Total Estimate
                            </span>
                          </div>
                          <span className="sm:text-4xl text-[#059F40] dark:text-[#22c95c] font-[800] font-['Plus_Jakarta_Sans',_sans-serif] text-[24px]" data-id="element-462" data-mp="3rmytv9gmh2c3">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      {(() => {
                const cartReady = isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !!deliveryTime && !!instructions.trim();
                const firstMissing = !isAddressValid ? 'a delivery address' : !isNameValid ? 'your name' : !isPhoneValid ? 'your phone number' : !isEmailValid ? 'your email' : !isDateValid ? 'a delivery date' : !deliveryTime ? 'a time window' : !instructions.trim() ? 'delivery instructions (where to drop the load)' : null;
                return <div className="space-y-4" data-id="element-463" data-mp="3l6fgtjkmx6yz">
                            {/* "Half-active" pattern: when required
                              fields are missing the button stays
                              clickable but wears a darker, muted green.
                              Tapping it runs the validation guard
                              (scrolls + focuses the first missing
                              field, which gets a green highlight
                              ring). Feels more helpful than a hard-
                              disabled gray button — the customer can
                              still tap it and is taken straight to
                              what they need to fix. */}
                            <button ref={checkoutCtaRef} type="submit" aria-disabled={!cartReady} className={`w-full flex items-center justify-center py-5 rounded-xl font-extrabold text-lg transition-all group text-white cursor-pointer ${cartReady ? 'bg-[#059F40] hover:bg-[#17A347] shadow-[inset_0_1px_0_rgba(255,255,255,0.2),0_4px_14px_rgba(5,159,64,0.4)]' : 'bg-[#0e4a2a] hover:bg-[#0e5a32] shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]'}`} data-id="element-464" data-mp="2m5j259rdf1rf">
                              Proceed to Checkout
                              <ArrowRight className={`w-6 h-6 ml-2 transition-transform ${cartReady ? 'group-hover:translate-x-1' : ''}`} data-id="element-465" data-mp="39vdbhhche86x" />
                            </button>
                            {!cartReady && firstMissing && <p className="text-center text-sm font-semibold text-amber-600 dark:text-amber-400 -mt-1" data-id="element-466" data-mp="1fhm5crtd26g8">
                                Add {firstMissing} to continue
                              </p>}
                            <div className="grid grid-cols-2 gap-4" data-id="element-467" data-mp="1bzogwqssa4t1">
                              <button type="button" onClick={handleContinueShopping} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-[#e5e5e5] dark:border-white/5 bg-white dark:bg-[#222222] backdrop-blur-sm hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-468" data-mp="176lssy6rznk8">
                                Add More
                              </button>
                              <button type="button" onClick={() => {
                      const cartText = items.map(item => {
                        const bumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                        const yardsLabel = bumped ? `${item.requestedQuantity} yds material (billed ${item.quantity})` : `${item.quantity} yds`;
                        return `${yardsLabel} - ${item.product} (${item.pricePerYard}/yd)`;
                      }).join('%0D%0A');
                      const yardsLine = yardsBilledExtra > 0 ? `Material Yards: ${materialYards}%0D%0ABilled Yards: ${totalYards} (3 yd order min)` : `Total Yards: ${totalYards}`;
                      const rc = getRegionMeta().code;
                      const mailtoUrl = `mailto:?subject=${rc} Gravel - My Cart Summary&body=Here is my cart summary from ${rc} Gravel:%0D%0A%0D%0A${cartText}%0D%0A%0D%0A${yardsLine}%0D%0AMaterials Total: ${totalPrice.toFixed(2)}%0D%0A%0D%0AView materials at: https://${rc}gravel.com`;
                      window.location.href = mailtoUrl;
                    }} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-[#e5e5e5] dark:border-white/5 bg-white dark:bg-[#222222] backdrop-blur-sm hover:bg-[#f5f5f5] dark:hover:bg-white/[0.06] transition-colors flex items-center justify-center dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]" data-id="element-469" data-mp="1lwaxv4hzobps">
                                <Mail className="w-4 h-4 mr-2" data-id="element-470" data-mp="2mt23s7q4gq7u" />
                                Email Cart
                              </button>
                            </div>
                          </div>;
              })()}
                      {/* "Need more than 12 yards or a custom amount?" line
                       removed — customers can already add multiple loads
                       or custom yardage directly from the product selector,
                       so the call-us message was misleading. */}
                    </div>}
                </form>}
              {/* ===== CHECKOUT VIEW ===== */}
              {view === 'checkout' && <div className="flex-1 overflow-y-auto relative" data-id="element-471" data-mp="3aimmqqm9znh4">
                  {/* Review header */}
                  <div className="sticky top-0 z-[60] bg-[#059F40] dark:bg-[#00704A] backdrop-blur-md border-b border-[#059F40] dark:border-[#00704A] px-8 py-4 shadow-sm" data-id="element-472" data-mp="3ohw0ljn0e5hx">
                    <div className="flex items-center gap-2" data-id="element-473" data-mp="3jh80m8gsyebk">
                      <ClipboardCheck className="w-5 h-5 text-white" data-id="element-474" data-mp="1ht3uwrg521ck" />
                      <h3 className="font-heading text-base font-extrabold text-white tracking-tight" data-id="element-475" data-mp="1msgixfiqxegq">
                        Review your order
                      </h3>
                    </div>
                  </div>

                  <div className="p-8" data-id="element-476" data-mp="1vk093d9big03">
                    <form id="checkout-form" onSubmit={handleSubmitOrder} className="space-y-8" data-id="element-477" data-mp="1vn4luz0umk2d">
                      {/* Order Summary */}
                      <div data-id="element-478" data-mp="13tcbgb6ursr6">
                        <h3 className="text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5 font-[Montserrat,_sans-serif]" data-id="element-479" data-mp="13unmmw2mo2sb">
                          Order Summary
                        </h3>
                        <div className="space-y-3" data-id="element-480" data-mp="1cspkx3uz8hbw">
                          {items.map(item => {
                      const materialQty = item.requestedQuantity ?? item.quantity;
                      const isBumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                      return <div key={item.id} className="flex justify-between items-start gap-3 text-base font-bold text-brand-charcoal dark:text-white py-3 border-b border-white/20 dark:border-white/5 last:border-0" data-id="element-481" data-mp="2yfqm2e1gzo5h">
                                {/* On mobile the product name and the quantity
                                 stack onto two lines; on desktop (sm+) they sit
                                 inline on a single line. Every piece of text in
                                 this row shares the same color, font, size and
                                 weight for a uniform look. */}
                                <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-2 min-w-0" data-id="element-482" data-mp="21nn09b1j6e9y">
                                  <span className="text-[18px] font-bold" data-id="element-483" data-mp="3xlo30dqh7qb">
                                    {item.product}
                                  </span>
                                  <span className="text-[12px] font-normal" data-id="element-484" data-mp="3vzjv7dz6m29r">
                                    × {materialQty} yds
                                    {isBumped && <span data-id="element-485" data-mp="1y5zarn3z7u4l">
                                        {' '}
                                        (billed {item.quantity} yd · 3 yd min)
                                      </span>}{' '}
                                    · ≈{' '}
                                    {(materialQty * getDensity(item.product)).toFixed(1)}{' '}
                                    tons
                                  </span>
                                </div>
                                <span className="shrink-0 text-[18px] font-bold text-brand-charcoal dark:text-white" data-id="element-486" data-mp="234smpb3m7d2n">
                                  $
                                  {(() => {
                            const surcharge = (() => {
                              const q = Math.max(3, item.quantity);
                              if (q >= 10) return 1;
                              return 1 + (10 - q) * 0.1;
                            })();
                            return (Math.max(3, item.quantity) * Math.ceil(item.pricePerYard * surcharge)).toFixed(2);
                          })()}
                                </span>
                              </div>;
                    })}
                          <div className="space-y-3 pt-3" data-id="element-487" data-mp="t6attzyllmhn">
                            <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-488" data-mp="t4pxq5wmxvho">
                              <span data-id="element-489" data-mp="incquwjddzr">Subtotal</span>
                              <span data-id="element-490" data-mp="jyekp6f8bo8">${totalPrice.toFixed(2)}</span>
                            </div>

                            {/* Promo Discount Row — rendered with the same font,
                             color, size and weight as the rest of the summary
                             (no green highlight), and only the promo name. */}
                            {promo.code && promo.discount > 0 && <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-491" data-mp="3dw8ly5zc6fm2">
                                <span data-id="element-492" data-mp="2sg2ydygilhwp">Promo {promo.code}</span>
                                <span data-id="element-493" data-mp="2xdbkub8p483r">−${promo.discount.toFixed(2)}</span>
                              </div>}

                            {/* Delivery Fee Row */}
                            <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-494" data-mp="37bioxthvyvoc">
                              <span data-id="element-495" data-mp="3bc1gdmfv9zq0">Delivery Fee</span>
                              <span data-id="element-496" data-mp="3gb5pye6pdzgv">
                                {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-497" data-mp="3g9kv0ngh8eih" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange" data-id="element-498" data-mp="3l4qfdhu8dl4g">
                                    Custom Quote
                                  </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500" data-id="element-499" data-mp="3v1v1xirznils">FREE</span> : deliveryFee > 0 ? <>
                                    {deliveryCount > 1 && <span className="mr-1.5" data-id="element-500" data-mp="2uol5lfj2l9a">
                                        ({deliveryCount} × $
                                        {deliveryFee.toFixed(2)})
                                      </span>}
                                    ${totalDeliveryFee.toFixed(2)}
                                  </> : <span data-id="element-501" data-mp="1efyjfhd5wvca">Based on address</span>}
                              </span>
                            </div>

                            {/* Saturday Surcharge Row */}
                            {saturdaySurcharge > 0 && <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-502" data-mp="223py2rm6dkgq">
                                <span data-id="element-503" data-mp="1xbonda4no1st">
                                  Saturday Delivery
                                  <span className="text-xs font-medium ml-1" data-id="element-504" data-mp="1scv9hglgi8s4">
                                    (weekend surcharge)
                                  </span>
                                </span>
                                <span data-id="element-505" data-mp="2l1sguoo4ql63">
                                  {deliveryCount > 1 && <span className="mr-1.5" data-id="element-506" data-mp="32qbwyzojun4w">
                                      ({deliveryCount} × $
                                      {SATURDAY_SURCHARGE_PER_LOAD.toFixed(2)})
                                    </span>}
                                  ${saturdaySurcharge.toFixed(2)}
                                </span>
                              </div>}

                            {/* Sales Tax Row — WA combined ~10.1% on materials */}
                            <div className="flex justify-between items-center text-lg font-extrabold text-brand-charcoal dark:text-white" data-id="element-507" data-mp="3lielex7fn7pp">
                              <span data-id="element-508" data-mp="3bpz31ys84h4a">
                                Sales Tax
                                <span className="text-xs font-medium ml-1" data-id="element-509" data-mp="37p5cexneco7p">
                                  (est. {(TAX_RATE * 100).toFixed(1)}%)
                                </span>
                              </span>
                              <span data-id="element-510" data-mp="3af9eynn4wop">${estimatedTax.toFixed(2)}</span>
                            </div>

                            <div className="flex justify-between items-center text-[18px] font-bold text-brand-charcoal dark:text-white pt-4 border-t border-white/20 dark:border-white/5" data-id="element-511" data-mp="1ly6yrkotzo37">
                              <span data-id="element-512" data-mp="1uxlo2gt9hsm4">Total</span>
                              <span data-id="element-513" data-mp="1uwsgxeqe513g">${finalTotal.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>

                        {/* First-time buyer FIRST10 nudge — only shown to
                         customers with no prior order on this device who have
                         NOT yet applied a promo. Once a promo is applied (e.g.
                         in the shopping-cart step) it lives in the breakdown's
                         Promo row above — we do NOT repeat it here as a second
                         "applied / Remove" chip. */}
                        {(() => {
                    const isFirstTimeBuyer = typeof window !== 'undefined' && !localStorage.getItem('253gravel_last_order');
                    // If a promo is already applied, it's shown in the
                    // breakdown above — render nothing here to avoid a
                    // duplicate callout.
                    if (promo.code) return null;
                    if (!isFirstTimeBuyer) return null;
                    return <div className="mt-4 flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-amber-50 dark:bg-[#1a1a1a] border border-amber-400/40 dark:border-amber-500/30" data-id="element-514" data-mp="xho2o32qyvai">
                              <div className="flex items-center gap-2.5 min-w-0" data-id="element-515" data-mp="179a97m45466x">
                                <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-400/90 text-stone-900 shrink-0 font-extrabold text-xs" data-id="element-516" data-mp="1l8thqg0v5jjr">
                                  10%
                                </span>
                                <div className="min-w-0" data-id="element-517" data-mp="3mzwupby87a7h">
                                  <p className="text-sm font-bold text-stone-900 dark:text-amber-200 leading-tight" data-id="element-518" data-mp="3i1d6f4w5tmby">
                                    First-time customer?
                                  </p>
                                  <p className="text-xs text-stone-600 dark:text-amber-300/80 leading-tight mt-0.5" data-id="element-519" data-mp="22gknmeam936n">
                                    Use code{' '}
                                    <span className="font-mono font-bold tracking-wider" data-id="element-520" data-mp="1srjacvvggzwd">
                                      FIRST10
                                    </span>{' '}
                                    for 10% off materials.
                                  </p>
                                </div>
                              </div>
                              <button type="button" onClick={() => applyPromoCode('FIRST10')} className="shrink-0 px-3 py-2 bg-stone-900 hover:bg-stone-800 dark:bg-amber-400 dark:hover:bg-amber-300 dark:text-stone-900 text-white rounded-lg text-xs font-extrabold uppercase tracking-wider transition-colors" data-id="element-521" data-mp="14vexzjyg8hha">
                                Apply
                              </button>
                            </div>;
                  })()}
                      </div>

                      {/* Delivery Details Summary (Read-Only) */}
                      <div className="mt-[30px]" data-id="element-522" data-mp="v41sx311u8le">
                        <div className="flex items-center justify-between mb-4" data-id="element-523" data-mp="v2qnyzvvausp">
                          <h3 className="text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight font-[Montserrat,_sans-serif]" data-id="element-524" data-mp="q5qlt0252c6v">
                            Delivery Details
                          </h3>
                          <button type="button" onClick={handleBackToCart} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-525" data-mp="gdktfp7z5sr5">
                            Edit
                          </button>
                        </div>
                        <div className="space-y-4" data-id="element-526" data-mp="2fmeoipqrub4">
                          <div className="flex items-start gap-3" data-id="element-527" data-mp="2hgzlx10jmf7">
                            <MapPin className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-528" data-mp="3tmyw8yuje9rk" />
                            <div data-id="element-529" data-mp="3jgpdn19kesl1">
                              {/* Address split into two lines: the street sits
                               on line 1 and the city / state / zip wrap onto
                               line 2. We split on the first comma so anything
                               after it (e.g. "Kent, WA, 98042") forms the
                               second line. Falls back to a single line if the
                               address has no comma. */}
                              {(() => {
                          const parts = addressInput.split(',');
                          const street = parts[0].trim();
                          const rest = parts.slice(1).map(p => p.trim()).filter(Boolean).join(', ');
                          return <p className="text-base text-brand-charcoal dark:text-white" data-id="element-530" data-mp="3fhyk144b5t2o">
                                    {street}
                                    {rest && <>
                                        <br data-id="element-531" data-mp="2mxhxqoxgwm0z" />
                                        {rest}
                                      </>}
                                  </p>;
                        })()}
                              {deliveryZone !== null ? <p className="text-sm text-green-600 dark:text-green-400 mt-0.5 font-medium" data-id="element-532" data-mp="2ooopouc2lksb">
                                  Zone {deliveryZone}
                                </p> : <p className="text-sm text-brand-orange mt-0.5 font-medium" data-id="element-533" data-mp="2jry9m709mp6w">
                                  Outside standard zones (Custom Quote)
                                </p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-534" data-mp="25z8d38n421vj">
                            <Phone className="w-5 h-5 text-brand-muted shrink-0" data-id="element-535" data-mp="20yufkkj4yabr" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-536" data-mp="3hdlupvr9tl34">
                              {phone}
                            </p>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-537" data-mp="3jy1uw8riwext">
                            <CalendarDays className="w-5 h-5 text-brand-muted shrink-0" data-id="element-538" data-mp="3jwqqq7u61566" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-539" data-mp="3oyg1dmdh7gtq">
                              {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        }) : 'No date selected'}
                              {deliveryTime && <>
                                  <span className="text-brand-muted dark:text-stone-400 mx-2" data-id="element-540" data-mp="3twhgxxbp0y0g">
                                    •
                                  </span>
                                  <span className="capitalize" data-id="element-541" data-mp="1q2jabbn85xc">
                                    {deliveryTime}
                                  </span>
                                </>}
                            </p>
                          </div>
                          {instructions && <div className="flex items-start gap-3 pt-3 border-t border-white/20 dark:border-white/5" data-id="element-542" data-mp="5jn1fp4jrz2k">
                              <MessageSquare className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-543" data-mp="paf97bos9n4" />
                              <p className="text-base text-brand-charcoal dark:text-stone-300 italic font-medium" data-id="element-544" data-mp="3rvk9fpidb4se">
                                "{instructions}"
                              </p>
                            </div>}
                        </div>
                      </div>

                      {/* Payment Method + Place Order */}
                      <div data-id="element-545" data-mp="ehz1ehrb097o">
                        <div className="flex items-baseline justify-between mb-3" data-id="element-546" data-mp="eh74n3lz05ft">
                          <h3 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white tracking-tight" data-id="element-547" data-mp="9inj34bagvnz">
                            Pay on Delivery
                          </h3>
                          <span className="text-xs text-stone-500 dark:text-stone-400 font-medium" data-id="element-548" data-mp="2u8uxonxv5dtc">
                            Online payment coming soon
                          </span>
                        </div>
                        <p className="text-sm text-brand-muted dark:text-stone-400 mb-3" data-id="element-549" data-mp="1erhwp7xdoht5">
                          Choose how you'll pay your driver, then place your
                          order
                        </p>
                        <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-550" data-mp="1oikzqf9vb6v2">
                          {([{
                      id: 'cash',
                      label: 'Cash',
                      icon: Banknote
                    }, {
                      id: 'card',
                      label: 'Card',
                      icon: CreditCard
                    }] as const).map(opt => {
                      const isActive = payOnDeliverySubMethod === opt.id;
                      const Icon = opt.icon;
                      return <button key={opt.id} type="button" onClick={() => {
                        setPaymentMethod('delivery');
                        setPayOnDeliverySubMethod(opt.id);
                      }} className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all ${isActive ? 'border-[#059F40] bg-[#059F40]/10 text-[#059F40] dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.1)]' : 'border-stone-200 dark:border-white/5 bg-white/50 dark:bg-[#1a1a1a] text-brand-charcoal dark:text-stone-200 hover:border-stone-300 dark:hover:border-white/20 dark:shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]'}`} aria-pressed={isActive} data-id="element-551" data-mp="d1lajsith859">
                                <Icon className="w-4 h-4 shrink-0" data-id="element-552" data-mp="mammj8m85rkv" />
                                <span data-id="element-553" data-mp="7f6u181am3ql">{opt.label}</span>
                              </button>;
                    })}
                        </div>
                        {(() => {
                    const placeOrderDisabled = !isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !instructions.trim() || isProcessingPayment;
                    const placeOrderReady = !placeOrderDisabled;
                    return <motion.button type="submit" disabled={placeOrderDisabled} animate={placeOrderReady ? {
                      boxShadow: ['inset 0 1px 0 rgba(255,255,255,0.2), 0 0 0 0 rgba(5,159,64,0.55), 0 4px 14px rgba(5,159,64,0.4)', 'inset 0 1px 0 rgba(255,255,255,0.2), 0 0 0 10px rgba(5,159,64,0), 0 4px 14px rgba(5,159,64,0.4)', 'inset 0 1px 0 rgba(255,255,255,0.2), 0 0 0 0 rgba(5,159,64,0), 0 4px 14px rgba(5,159,64,0.4)']
                    } : {
                      boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 1px 2px rgba(5,159,64,0.15)'
                    }} transition={placeOrderReady ? {
                      duration: 1.8,
                      repeat: Infinity,
                      ease: 'easeOut'
                    } : {
                      duration: 0.2
                    }} whileTap={placeOrderReady ? {
                      scale: 0.98
                    } : undefined} className="relative w-full flex items-center justify-center gap-2 px-4 py-7 rounded-lg text-base font-bold transition-colors bg-[#059F40] hover:bg-[#17A347] text-white disabled:opacity-50 disabled:cursor-not-allowed mb-5" data-id="element-554" data-mp="1f98c668soy7v">
                              {isProcessingPayment ? <>
                                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-555" data-mp="2mkzl0yekh9g1" />
                                  <span data-id="element-556" data-mp="2mn293cw5ssi4">Processing</span>
                                </> : <>
                                  <Check className="w-5 h-5" strokeWidth={3} data-id="element-557" data-mp="1ozbq6hxrez6m" />
                                  <span data-id="element-558" data-mp="1twu0abnijkg7">Place Order</span>
                                </>}
                            </motion.button>;
                  })()}
                        {/* Submission error — surfaces a real, actionable
                         message when an order fails to reach dispatch
                         (network, EmailJS rate-limit, etc.) instead of
                         silently failing. Includes a tap-to-call so the
                         customer can still book over the phone. */}
                        {orderError && !isProcessingPayment && <motion.div initial={{
                    opacity: 0,
                    y: -4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="mt-3 rounded-xl border border-red-400/60 bg-red-50 dark:bg-red-950/30 p-4" role="alert" aria-live="polite" data-id="element-559" data-mp="yngq0it7e03u">
                            <div className="flex items-start gap-3" data-id="element-560" data-mp="jz3o757fpn1f">
                              <Info className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" data-id="element-561" data-mp="fw7czzk84t7i" />
                              <div className="flex-1 min-w-0" data-id="element-562" data-mp="awugen3jw7rq">
                                <p className="text-sm font-bold text-red-700 dark:text-red-300 mb-1" data-id="element-563" data-mp="azgwxc8f5xm3">
                                  Couldn't place your order
                                </p>
                                <p className="text-sm text-red-700/90 dark:text-red-200/90 leading-relaxed mb-3" data-id="element-564" data-mp="259au28yoeo6m">
                                  {orderError}
                                </p>
                                <a href="tel:206-619-3742" className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors" data-id="element-565" data-mp="xxbidpn7m47b">
                                  <Phone className="w-3.5 h-3.5" data-id="element-566" data-mp="o3cf2nbegeds" />
                                  Call (206) 619-3742
                                </a>
                              </div>
                            </div>
                          </motion.div>}
                      </div>

                      {/* SMS Opt-in */}
                      <div className="p-6 border-b border-stone-100 dark:border-stone-800" data-id="element-567" data-mp="5b0eo64b3n6a">
                        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-568" data-mp="a91zavhdrujv">
                          <div className="relative flex items-center justify-center mt-0.5" data-id="element-569" data-mp="3rhyrbd2sr8sx">
                            <input type="checkbox" checked={smsOptIn} onChange={e => setSmsOptIn(e.target.checked)} className="peer sr-only" data-id="element-570" data-mp="be1txse48y0" />
                            <div className="w-5 h-5 border-2 border-stone-300 dark:border-stone-600 rounded transition-colors peer-checked:bg-brand-orange peer-checked:border-brand-orange group-hover:border-brand-orange" data-id="element-571" data-mp="3cvrm42bnczrt"></div>
                            <Check className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" data-id="element-572" data-mp="3muh2trrt4e6h" />
                          </div>
                          <div data-id="element-573" data-mp="3vsb0p757zfvr">
                            <span className="text-sm font-medium text-stone-900 dark:text-white block mb-0.5" data-id="element-574" data-mp="3rs8waq83o8cs">
                              Send me SMS updates about this delivery
                            </span>
                            <span className="text-xs text-stone-500 dark:text-stone-400" data-id="element-575" data-mp="4fge145lly96">
                              Get notified when your driver is assigned, en
                              route, and delivered.
                            </span>
                          </div>
                        </label>
                      </div>

                      {/* Total reminder */}
                      <div className="px-6 pb-6" data-id="element-576" data-mp="qfnef4p5cv2t">
                        <div className="flex items-center justify-between text-sm" data-id="element-577" data-mp="qgf5q3bpo6na">
                          <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-578" data-mp="vdp5abstdyou">
                            Order total
                          </span>
                          <span className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-579" data-mp="154tkncmejlk7">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW — fallback (no summary data) =====
               Safety net: handleSubmitOrder only flips view→'success'
               AFTER setOrderSummaryData runs AND the email was accepted.
               But if it somehow happens (state leak, future refactor),
               show a real message + a way to reach dispatch instead of
               a blank screen behind the "Order Confirmed" header. */}
              {view === 'success' && !orderSummaryData && <div className="flex-1 overflow-y-auto p-8" data-id="element-580" data-mp="33m3qi7bungff">
                  <div className="flex flex-col items-center text-center space-y-6 max-w-md mx-auto py-8" data-id="element-581" data-mp="33k95yu4hpqbv">
                    <div className="w-20 h-20 bg-amber-100 dark:bg-[#1a1a1a] rounded-full flex items-center justify-center" data-id="element-582" data-mp="2yl62vf82lu6j">
                      <Info className="w-10 h-10 text-amber-600 dark:text-amber-400" data-id="element-583" data-mp="3rc7ljqaz27ut" />
                    </div>
                    <div data-id="element-584" data-mp="3mcbne7oyhan6">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white mb-2" data-id="element-585" data-mp="3mbjs6xz9texf">
                        We may have lost your order details
                      </h3>
                      <p className="text-base text-brand-muted dark:text-stone-300" data-id="element-586" data-mp="2az9b3otw378t">
                        Your confirmation didn't load on this device. If you
                        placed an order, please call us to verify it went
                        through — we'll find it on our end.
                      </p>
                    </div>
                    <div className="flex flex-col gap-3 w-full" data-id="element-587" data-mp="2mmc11zltqv6x">
                      <a href="tel:206-619-3742" className="w-full py-3.5 bg-brand-orange hover:bg-brand-orangeHover text-white font-bold rounded-xl transition-colors flex items-center justify-center gap-2" data-id="element-588" data-mp="2mnvgxp3kj486">
                        <Phone className="w-5 h-5" data-id="element-589" data-mp="3efbci8n3s1aj" />
                        Call (206) 619-3742
                      </a>
                      <button type="button" onClick={() => {
                  setView('cart');
                  setOrderError('');
                }} className="w-full py-3 bg-stone-200 dark:bg-[#1a1a1a] hover:bg-stone-300 dark:hover:bg-white/10 text-brand-charcoal dark:text-white font-bold rounded-xl transition-colors" data-id="element-590" data-mp="35l6glat43zo5">
                        Back to cart
                      </button>
                    </div>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW ===== */}
              {view === 'success' && orderSummaryData && <div className="flex-1 overflow-y-auto p-8" data-id="element-591" data-mp="1lo7q4juniw84">
                  <div className="flex flex-col items-center text-center mb-8" data-id="element-592" data-mp="20e6k0af6blhs">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 20
              }} className="w-24 h-24 bg-green-100 dark:bg-[#1a1a1a] rounded-full flex items-center justify-center mb-6 relative" data-id="element-593" data-mp="1vek6s0zci58z">
                      <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" data-id="element-594" data-mp="1hl2qqq5vbanp" />
                      <motion.div initial={{
                  scale: 1,
                  opacity: 0.6
                }} animate={{
                  scale: 1.6,
                  opacity: 0
                }} transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} className="absolute inset-0 rounded-full border-2 border-green-500" data-id="element-595" data-mp="1cq5ivmkbr7ce" />
                    </motion.div>
                    <h3 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-596" data-mp="rjlzk9gf9ro9">
                      Order Received!
                    </h3>
                    <p className="text-lg text-brand-muted dark:text-stone-300 mb-4 max-w-md" data-id="element-597" data-mp="3vx2abzw0b58j">
                      Thank you, {orderSummaryData.name}. We've received your
                      order request for{' '}
                      {orderSummaryData.materialYards ?? orderSummaryData.totalYards}{' '}
                      yards of material
                      {orderSummaryData.yardsBilledExtra > 0 && <span className="text-amber-600 dark:text-amber-400" data-id="element-598" data-mp="1k7rmn2uy7sr9">
                          {' '}
                          (billed as {orderSummaryData.totalYards} yd — 3 yd
                          order min)
                        </span>}
                      .
                    </p>
                    <div className="inline-flex items-center gap-2 bg-brand-orange/10 border border-brand-orange/20 px-4 py-2 rounded-full" data-id="element-599" data-mp="1rraoqrfvwcpp">
                      <span className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider" data-id="element-600" data-mp="1dtc97iptua8m">
                        Confirmation #
                      </span>
                      <span className="font-mono font-extrabold text-brand-orange" data-id="element-601" data-mp="26hrm9zpeeihb">
                        {orderSummaryData.orderNumber}
                      </span>
                    </div>
                  </div>

                  {/* Optional drop-site photo — moved here from checkout so
                   the form stays fast. Customer can take their time and
                   we send it to dispatch as a follow-up tied to the
                   order number. The Property & Vehicle Safety reminder now
                   sits alongside it (moved here from the checkout form) so
                   the constraint lands with the drop-site photo prompt — and
                   it's also repeated in the confirmation email. */}
                  <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 items-start" data-id="element-602" data-mp="2z0tzczl2ccww">
                    <PostOrderDropSitePhoto orderNumber={orderSummaryData.orderNumber} customerName={orderSummaryData.name} customerEmail={orderSummaryData.email} address={orderSummaryData.addressInput} data-id="element-603" data-mp="2u20q5jct782j" />
                    <div className="flex items-start gap-2.5 p-4 rounded-2xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-left h-full" data-id="element-604" data-mp="2l58hrdh5svei">
                      <Info className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" data-id="element-605" data-mp="2bdvmebw5jn3q" />
                      <div data-id="element-606" data-mp="26fu7821uk7xj">
                        <h5 className="text-xs font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300 mb-1" data-id="element-607" data-mp="26cep0qcvmirw">
                          Property & Vehicle Safety
                        </h5>
                        <p className="text-xs text-amber-900/80 dark:text-amber-200/80 leading-relaxed" data-id="element-608" data-mp="cvisri4oi5wt">
                          We cannot drive across irrigated, wet, frozen, or
                          landscaped areas to reach the dump spot. Please make
                          sure your instructions keep our truck on gravel or
                          paved surfaces.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* What happens next — stepped timeline */}
                  <div className="bg-[#e6f5ea] dark:bg-[#222222] border border-[#059F40]/25 dark:border-white/5 rounded-2xl p-6 mb-6 text-left" data-id="element-609" data-mp="3uctlh49aqyxa">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-5 flex items-center gap-2" data-id="element-610" data-mp="33w6mqqsymfa">
                      <MessageSquare className="w-5 h-5 text-brand-orange" data-id="element-611" data-mp="36mphuww3yz6a" />
                      What happens next?
                    </h4>
                    <div className="space-y-4" data-id="element-612" data-mp="3bk7scrprxmge">
                      {[{
                  icon: Send,
                  title: 'SMS confirmation',
                  desc: <>
                              Text confirmation arriving at{' '}
                              <strong className="text-brand-charcoal dark:text-white" data-id="element-613" data-mp="1qv3rqo1xye5u">
                                {formatPhone(orderSummaryData.phone)}
                              </strong>{' '}
                              within a few minutes.
                            </>,
                  active: true
                }, {
                  icon: ClipboardCheck,
                  title: 'Dispatch review',
                  desc: 'Our team verifies your address, materials, and delivery window — usually within 1 business hour.'
                }, {
                  icon: Truck,
                  title: 'Driver dispatched',
                  desc: 'On delivery day, we text you a live tracking link the moment your driver is en route.'
                }].map((step, i) => {
                  const Icon = step.icon;
                  return <div key={i} className="flex gap-4" data-id="element-614" data-mp="1a6fa8yfbr4f" data-mpi={i}>
                            <div className="flex flex-col items-center" data-id="element-615" data-mp="5c0cg79euczu">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${step.active ? 'bg-[#059F40] text-white shadow-[0_0_12px_rgba(5,159,64,0.4)]' : 'bg-stone-200 dark:bg-[#1a1a1a] text-stone-500 dark:text-stone-400'}`} data-id="element-616" data-mp="5b8dqnkinz5c">
                                <Icon className="w-4 h-4" data-id="element-617" data-mp="3dqs5tj9gbbit" />
                              </div>
                              {i < 2 && <div className="w-px flex-1 bg-stone-200 dark:bg-white/5 my-1 min-h-[18px]" data-id="element-618" data-mp="3irzavak7lwls" />}
                            </div>
                            <div className="pb-1 flex-1" data-id="element-619" data-mp="34thu5wsq1kdd">
                              <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-620" data-mp="39r178tdonr3q">
                                {step.title}
                              </p>
                              <p className="text-sm text-brand-muted dark:text-stone-400 mt-0.5 leading-relaxed" data-id="element-621" data-mp="17z4x9o75ypb2">
                                {step.desc}
                              </p>
                            </div>
                          </div>;
                })}
                    </div>
                  </div>

                  {/* Order details card */}
                  <div className="bg-[#e6f5ea] dark:bg-[#222222] border border-[#059F40]/25 dark:border-white/5 rounded-2xl p-6 mb-6 text-left" data-id="element-622" data-mp="wlcwzjb67zpu">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-623" data-mp="1p75v3fliuq6a">
                      Order Details
                    </h4>
                    <div className="space-y-3 mb-4" data-id="element-624" data-mp="1kbgs12aamr58">
                      {orderSummaryData.items.map((item: any, i: number) => <div key={i} className="flex justify-between text-sm font-medium" data-id="element-625" data-mp="1ajayvp8b23og" data-mpi={i}>
                          <span className="text-brand-charcoal dark:text-stone-200" data-id="element-626" data-mp="1ahgnp2mu5pxk">
                            {item.quantity} yds — {item.product}
                          </span>
                        </div>)}
                    </div>
                    <div className="space-y-2 text-sm font-medium border-t border-stone-200 dark:border-white/5 pt-4 mb-4" data-id="element-627" data-mp="2ajwoeuqmm3lw">
                      <div className="flex justify-between" data-id="element-628" data-mp="2fiq1wn5wxum2">
                        <span className="text-brand-muted dark:text-white" data-id="element-629" data-mp="2kgrj12js3juw">
                          Materials Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-630" data-mp="2php2rjkfslyi">
                          ${orderSummaryData.totalPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-631" data-mp="2yezch0hlw31b">
                        <span className="text-brand-muted dark:text-white" data-id="element-632" data-mp="338tsnty05zvn">
                          Delivery Fee
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-633" data-mp="388pxtwifrxcs">
                          {orderSummaryData.deliveryFeeLabel}
                        </span>
                      </div>
                      {orderSummaryData.saturdaySurcharge > 0 && <div className="flex justify-between" data-id="element-634" data-mp="6ccplvkle9zh">
                          <span className="text-brand-muted dark:text-white" data-id="element-635" data-mp="6eys3gsmlzau">
                            Saturday Delivery
                          </span>
                          <span className="text-brand-charcoal dark:text-white" data-id="element-636" data-mp="g9z8et50iloq">
                            ${orderSummaryData.saturdaySurcharge.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between" data-id="element-637" data-mp="3jsaaj22k4pqe">
                        <span className="text-brand-muted dark:text-white" data-id="element-638" data-mp="3etpcn9as2i54">
                          Sales Tax
                          <span className="text-xs text-white/70 ml-1" data-id="element-639" data-mp="3tilsvtmzn1gt">
                            (est. {(TAX_RATE * 100).toFixed(1)}%)
                          </span>
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-640" data-mp="31081ocw5qdqp">
                          ${(orderSummaryData.estimatedTax ?? 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200 dark:border-white/5" data-id="element-641" data-mp="1bj2riv2jjstt">
                        <span className="text-brand-charcoal dark:text-white" data-id="element-642" data-mp="12jpho1ai928k">
                          Estimated Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-643" data-mp="16m404ka9hzzb">
                          ${orderSummaryData.finalTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm font-medium border-t border-stone-200 dark:border-white/5 pt-4" data-id="element-644" data-mp="1z7vo8tqzm4ph">
                      <div className="flex items-start gap-3" data-id="element-645" data-mp="1q9a2yqu4sbl0">
                        <MapPin className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-646" data-mp="1q7gt72o81b8s" />
                        <span className="text-brand-charcoal dark:text-white" data-id="element-647" data-mp="9tgv7x8xrvo6">
                          {orderSummaryData.addressInput}
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-648" data-mp="391hfppwx83dt">
                        <CalendarDays className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-649" data-mp="3e0u9kum7mapw" />
                        <span className="text-brand-charcoal dark:text-white" data-id="element-650" data-mp="3j0q505gucttu">
                          {new Date(orderSummaryData.deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}{' '}
                          •{' '}
                          <span className="capitalize" data-id="element-651" data-mp="2v8rs5h1quqel">
                            {orderSummaryData.timeLabel}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-652" data-mp="3450p8p291cxk">
                        <Phone className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-653" data-mp="27kia8q7nimsq" />
                        <span className="text-brand-charcoal dark:text-white" data-id="element-654" data-mp="2bltqi54mxvo9">
                          {formatPhone(orderSummaryData.phone)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Prep tip link */}
                  <button type="button" onClick={() => {
              handleCloseSuccess();
              navigate('/delivery-prep');
            }} className="w-full flex items-center justify-between bg-[#f0faf3] dark:bg-[#222222] border border-[#059F40]/25 dark:border-white/5 rounded-xl p-4 mb-3 hover:bg-[#e6f5ea] dark:hover:bg-white/10 transition-colors text-left" data-id="element-655" data-mp="1v1weseg3ll25">
                    <div className="flex items-center gap-3" data-id="element-656" data-mp="1beonxh5rqn76">
                      <Info className="w-5 h-5 text-brand-orange shrink-0" data-id="element-657" data-mp="1beydx4qtcucv" />
                      <div data-id="element-658" data-mp="17e4t4k8o3vo7">
                        <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-659" data-mp="17exwrccby524">
                          How to prep for delivery
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400" data-id="element-660" data-mp="xg8gtp3zuuoi">
                          Driveway clearance, dump zone, what to expect
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-brand-muted" data-id="element-661" data-mp="so1ox3ia4x77" />
                  </button>

                  {/* Action button */}
                  <button onClick={handleCloseSuccess} className="w-full bg-brand-charcoal dark:bg-[#1a1a1a] hover:bg-brand-charcoal/90 dark:hover:bg-white/10 text-white py-4 rounded-xl font-extrabold text-lg transition-colors border border-transparent dark:border-white/5 backdrop-blur-sm mt-4" data-id="element-662" data-mp="irf4ugxs2a68">
                    Done
                  </button>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}